/**
 * Página principal do Chat do Tenant
 * 
 * Interface de atendimento omnichannel com lista de conversas,
 * área de mensagens e sidebar de detalhes do contato.
 * Suporta envio e recebimento de texto e áudio.
 * 
 * Rastreabilidade: IDs de correlação enviados em todas as chamadas send-message
 * @version 2024.12.06 - Rebuild forçado para garantir deploy com observabilidade completa
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { toast } from 'sonner';
import { showToast } from '@/lib/toast';
// Conversão de áudio no cliente (OGG/Opus -> MP3) para inboxes Meta WABA
import { prepareAudioForProvider, providerRequiresMp3 } from '@/lib/audio/convertToMp3';
import { ConversationList } from '@/components/tenant/chat/ConversationList';
import { ChatArea } from '@/components/tenant/chat/ChatArea';
import { ContactSidebar } from '@/components/tenant/chat/ContactSidebar';
import { GroupSidebar } from '@/components/tenant/chat/GroupSidebar';
import { SendTemplateDialog } from '@/components/tenant/chat/SendTemplateDialog';
import { Database } from '@/integrations/supabase/types';
import { useProviderUtils } from '@/hooks/useProviderUtils';
import { useContactPresence } from '@/hooks/useContactPresence';
import { useSessionWindow } from '@/hooks/useSessionWindow';
import { isEligibleForSessionWindow } from '@/lib/instagramSessionWindow';
import { useBlacklist } from '@/hooks/useBlacklist';
import { usePendingTransfer } from '@/hooks/usePendingTransfer';
import { useConversationList } from '@/hooks/useConversationList';
import type { ConversationListFilters } from '@/hooks/useConversationList';
// blobToBase64 não é mais necessário - áudio sempre em OGG via opus-media-recorder
import { createLogger } from '@/lib/logger';
import { createErrorMetadata } from '@/lib/errorMetadata';
// Hook que encapsula o lifecycle de mensagens outbound via RPCs seguras
// (substitui INSERT/UPDATE diretos em messages e message_attachments)
import { useOutboundMessageLifecycle } from '@/hooks/useOutboundMessageLifecycle';
import { registerSendError } from '@/hooks/useErrorCatalog';
import { getStorageUrl } from '@/lib/storage';

// Logger para o módulo de Chat (logs condicionais por ambiente)
const chatLogger = createLogger('Chat');

// Ícone para modo observador
import { Eye, EyeOff, Loader2 as LoaderIcon } from 'lucide-react';

/**
 * Gera delay aleatório para indicador de digitação/gravação
 * Usado para simular tempo de digitação antes de enviar mensagem
 * @param min - Valor mínimo em segundos
 * @param max - Valor máximo em segundos
 * @returns Número inteiro aleatório entre min e max (inclusive)
 */
const getRandomDelay = (min: number, max: number): number => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

type ConversationStatus = Database['public']['Enums']['conversation_status'];
type MessageDirection = Database['public']['Enums']['message_direction'];
type MessageType = Database['public']['Enums']['message_type'];
type MessageStatus = Database['public']['Enums']['message_status'];
type SenderType = Database['public']['Enums']['sender_type'];

export interface Contact {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  avatar_url: string | null;
  tags: Array<{ id: string; name: string; color: string }>;
  is_saved_to_agenda?: boolean; // Indica se foi salvo na agenda do WhatsApp
  metadata?: Record<string, unknown> | null; // Metadata do contato (inclui username para Instagram)
  /** Identificadores de canal separados do telefone (IGSID, PSID, visitor_id) */
  channel_ids?: {
    instagram_igsid?: string;
    instagram_username?: string;
    facebook_psid?: string;
    webchat_visitor_id?: string;
  } | null;
}

/** Interface para grupo WhatsApp */
export interface ChatGroup {
  id: string;
  name: string;
  description: string | null;
  avatar_url: string | null;
  participant_count: number;
  external_id: string; // JID do grupo (ex: "120363@g.us")
  is_readonly: boolean | null; // Grupo somente leitura
  is_announcement: boolean | null; // Grupo de anúncios (apenas admins podem enviar)
  /** Indica se distribuição automática está ativa para este grupo */
  auto_distribution_enabled?: boolean | null;
}

export interface Conversation {
  id: string;
  inbox_id: string;
  /** ID do contato (nullable para grupos) */
  contact_id: string | null;
  /** Dados do contato (nullable para grupos) */
  contact: Contact | null;
  /** ID do grupo (nullable para conversas individuais) */
  group_id: string | null;
  /** Dados do grupo (nullable para conversas individuais) */
  group: ChatGroup | null;
  inbox: {
    id: string;
    name: string;
    channel_type: string;
    identifier: string;  // Número de telefone/identificador da inbox
    settings?: {
      signature_enabled?: boolean;
      signature_format?: string;
      [key: string]: unknown;
    } | null;
    /** Slug do provider para validação de features (ex: etiquetas) */
    provider_slug?: string | null;
  };
  assigned_to: string | null;
  assigned_agent?: {
    id: string;
    full_name: string | null;
    avatar_url: string | null;
  };
  /** ID do departamento associado à conversa */
  department_id: string | null;
  /** Dados do departamento (preenchido via join) */
  department?: {
    id: string;
    name: string;
  } | null;
  /** ID da fase do Kanban (pode ser null se não vinculado) */
  kanban_phase_id?: string | null;
  /** Dados da fase do Kanban (preenchido via join) */
  kanban_phase?: {
    id: string;
    name: string;
    color: string;
    category?: {
      id: string;
      name: string;
      color: string;
    } | null;
  } | null;
  status: ConversationStatus;
  last_message_at: string | null;
  last_message_preview: string | null;
  unread_count: number;
  created_at: string;
  /** Indica se conversa tem mensagens além de campanha (false = somente campanha) */
  has_non_campaign_messages?: boolean;
  /** Timestamp da última mensagem inbound (para cálculo janela 24h WhatsApp Oficial) */
  last_inbound_at?: string | null;
  /** Indica se o bot está pausado nesta conversa (Human Takeover) */
  bot_paused?: boolean;
  /** Timestamp de quando o bot foi pausado */
  bot_paused_at?: string | null;
  /** Timestamp até quando o bot deve ficar pausado */
  bot_paused_until?: string | null;
  
  // ===== CAMPOS PARA COMENTÁRIOS INSTAGRAM =====
  /** Tipo de origem da conversa: direct (DM), comment, mention, story */
  source_type?: 'direct' | 'comment' | 'mention' | 'story' | null;
  /** ID da mídia/postagem (para comentários) */
  source_media_id?: string | null;
  /** URL da thumbnail da postagem */
  source_media_url?: string | null;
  /** ID do comentário original */
  source_comment_id?: string | null;
  /** v1.2.0: conversa fixada pelo usuário logado (vinda de conversation_pins) */
  is_pinned?: boolean;
}

// Interface para anexos de mensagem
export interface MessageAttachment {
  id: string;
  type: string;
  url: string;
  mime_type: string | null;
  filename: string | null;
  size_bytes: number | null;
  // Metadata pode ser qualquer JSON ou null
  metadata: Record<string, unknown> | null;
}

// Interface para reação em mensagem (importada do componente para consistência)
export interface MessageReaction {
  emoji: string;
  reacted_by: string;  // Telefone de quem reagiu
  reacted_at: number;  // Timestamp
}

export interface Message {
  id: string;
  conversation_id: string;
  sender_type: SenderType;
  sender_id: string | null;
  content: string | null;
  message_type: MessageType;
  direction: MessageDirection;
  status: MessageStatus;
  sent_at: string;
  delivered_at: string | null;
  read_at: string | null;
  external_id: string | null;
  attachments?: MessageAttachment[];
  /** ID da mensagem que está sendo respondida */
  reply_to_id?: string | null;
  /** Dados da mensagem respondida (populated via join) */
  reply_to?: {
    id: string;
    content: string | null;
    message_type: MessageType;
    sender_type: SenderType;
    attachments?: MessageAttachment[];
  } | null;
  /** Reações à mensagem */
  reactions?: MessageReaction[] | null;
  /** Metadata da mensagem (inclui z_api_phone para reações) */
  metadata?: Record<string, unknown> | null;
  /** Data/hora da exclusão (soft delete) - se preenchido, mensagem foi apagada */
  deleted_at?: string | null;
  /** Telefone do participante (usado em grupos para identificar remetente) */
  participant_phone?: string | null;
  /** Dados do remetente (agente) - populated via join com profiles */
  sender?: {
    id: string;
    full_name: string | null;
    avatar_url: string | null;
  } | null;
}

// Interface para dados de navegação vindos da página de Contatos
interface NavigationState {
  contactId?: string;
  contactPhone?: string;
  contactName?: string;
  inboxId?: string;
}

// Interface para departamento (usado no filtro)
interface Department {
  id: string;
  name: string;
}

/**
 * Aplica assinatura do agente NO INÍCIO da mensagem se configurado na inbox
 * @param content Conteúdo original da mensagem
 * @param inboxSettings Configurações da inbox (signature_enabled, signature_format)
 * @param agentName Nome do agente logado
 * @returns Conteúdo com assinatura aplicada ou original se desabilitado
 */
const applySignature = (
  content: string,
  inboxSettings: { signature_enabled?: boolean; signature_format?: string; [key: string]: unknown } | null | undefined,
  agentName: string | null | undefined
): string => {
  // Se assinatura desabilitada ou nome não disponível, retorna conteúdo original
  if (!inboxSettings?.signature_enabled || !agentName) {
    return content;
  }
  
  // Formato padrão: nome em negrito + itálico com dois pontos
  const format = inboxSettings.signature_format || '_*{{agent_name}}:*_';
  const signature = format.replace('{{agent_name}}', agentName);
  
  // Assinatura no início + linha em branco + conteúdo
  return `${signature}\n\n${content}`;
};

const TenantChat = () => {
  const { currentTenant, user, profile } = useAuth();
  // Hook de permissões para verificar role do usuário (agent vê apenas suas conversas)
  const { userRole, loading: permissionsLoading } = usePermissions();
  // Lifecycle de mensagens outbound (Fase 3: RPCs ao redor da edge function send-message)
  const {
    createPending,
    markSent,
    markFailed,
    attachMedia,
    setReaction,
    softDelete,
    resetForRetry,
    createSystem,
  } = useOutboundMessageLifecycle();
  const { sendTypingIndicator, markMessageAsRead, checkPhoneExists, sendReaction, deleteMessage } = useProviderUtils();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  // Ref para evitar stale closure em callbacks (realtime, etc.)
  const selectedConversationRef = useRef<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  // Loading agora é controlado pelo hook useConversationList (isLoading)
  // Mantém loading local apenas para controle do initChatLoad (dados auxiliares)
  const [auxiliaryLoading, setAuxiliaryLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  // ===== PERSISTÊNCIA DO SIDEBAR DIREITO (DETALHES DO CONTATO) =====
  // Chave para persistência do estado do sidebar direito
  const CONTACT_SIDEBAR_STORAGE_KEY = 'contact_sidebar:state';
  
  /**
   * Obtém o estado inicial do sidebar de contato do localStorage
   * Por padrão, sidebar FECHADO (false) - diferente do esquerdo que é aberto
   * @returns boolean - true para expandido, false para recolhido
   */
  const getInitialContactSidebarState = (): boolean => {
    if (typeof window === 'undefined') return false;
    try {
      const stored = localStorage.getItem(CONTACT_SIDEBAR_STORAGE_KEY);
      if (stored !== null) {
        return stored === 'true';
      }
    } catch (error) {
      // Fallback silencioso em caso de erro de acesso ao localStorage
    }
    return false; // Valor padrão: RETRAÍDO
  };
  
  const [showContactSidebar, setShowContactSidebar] = useState(() => getInitialContactSidebarState());
  
  /**
   * Alterna visibilidade do sidebar de contato e persiste preferência
   * Salva no localStorage para manter escolha do usuário entre sessões
   */
  const handleToggleContactSidebar = useCallback(() => {
    setShowContactSidebar(prev => {
      const newState = !prev;
      try {
        localStorage.setItem(CONTACT_SIDEBAR_STORAGE_KEY, String(newState));
      } catch (error) {
        // Fallback silencioso
      }
      return newState;
    });
  }, []);
  
  /**
   * Fecha o sidebar de contato e persiste preferência
   * Usado pelo botão X do ContactSidebar e GroupSidebar
   */
  const handleCloseContactSidebar = useCallback(() => {
    setShowContactSidebar(false);
    try {
      localStorage.setItem(CONTACT_SIDEBAR_STORAGE_KEY, 'false');
    } catch (error) {
      // Fallback silencioso - localStorage indisponível
    }
  }, []);
  
  // ===== ESTADO PARA NAVEGAÇÃO MOBILE (MASTER-DETAIL) =====
  // Em mobile, controla se exibe a lista de conversas ('list') ou a área de chat ('chat')
  const isMobile = useIsMobile();
  const [mobileView, setMobileView] = useState<'list' | 'chat'>('list');
  // ===== DEBOUNCE NO CAMPO DE PESQUISA =====
  // searchInput: valor visual do input (atualiza a cada tecla, sem delay)
  // debouncedSearch: valor usado na queryKey (atualiza após 900ms sem digitação)
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Sincroniza searchInput -> debouncedSearch com debounce de 900ms
  // Mínimo de 2 caracteres para disparar busca (evita queries amplas)
  // Limpar campo reseta imediatamente (sem esperar debounce)
  useEffect(() => {
    // Se campo vazio, reseta imediatamente
    if (!searchInput) {
      setDebouncedSearch('');
      return;
    }
    // Só dispara busca com 2+ caracteres
    if (searchInput.length < 2) return;

    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 900);

    return () => clearTimeout(timer);
  }, [searchInput]);
  
  // ===== CACHE LOCAL PARA FILTROS RÁPIDOS =====
  // Chave única no localStorage para persistir filtros entre navegações
  const CHAT_FILTERS_KEY = 'chat_filters_v2';
  
  // Interface para tipagem do cache de filtros
  interface ChatFiltersCache {
    statusFilters: (ConversationStatus | 'all')[];  // Array para multi-select
    conversationTypeFilter: 'all' | 'individual' | 'group';
    readStatusFilter: 'all' | 'read' | 'unread';
    departmentFilter: string;
    /** Escopo rápido v1.1.0: meus/todos/bot */
    scopeFilter?: 'all' | 'mine' | 'bot';
  }
  
  // Função para carregar filtros do localStorage
  const loadFiltersFromCache = (): ChatFiltersCache => {
    try {
      const cached = localStorage.getItem(CHAT_FILTERS_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        // Valida estrutura do cache
        if (parsed && Array.isArray(parsed.statusFilters)) {
          return parsed;
        }
      }
    } catch (e) {
      // Ignora erro de parse, usa valores padrão
      chatLogger.warn('Erro ao carregar filtros do cache', e);
    }
    // Valores padrão
    return {
      statusFilters: ['all'],
      conversationTypeFilter: 'individual',
      readStatusFilter: 'all',
      departmentFilter: 'all',
      scopeFilter: 'all'
    };
  };
  
  // Estados dos filtros inicializados a partir do cache
  const [statusFilters, setStatusFilters] = useState<(ConversationStatus | 'all')[]>(
    () => loadFiltersFromCache().statusFilters
  );
  const [departmentFilter, setDepartmentFilter] = useState<string | 'all'>(
    () => loadFiltersFromCache().departmentFilter
  );
  const [conversationTypeFilter, setConversationTypeFilter] = useState<'all' | 'individual' | 'group'>(
    () => loadFiltersFromCache().conversationTypeFilter
  );
  const [readStatusFilter, setReadStatusFilter] = useState<'all' | 'read' | 'unread'>(
    () => loadFiltersFromCache().readStatusFilter
  );
  // v1.1.0 - Escopo rápido: meus/todos/bot
  const [scopeFilter, setScopeFilter] = useState<'all' | 'mine' | 'bot'>(
    () => loadFiltersFromCache().scopeFilter || 'all'
  );
  // Lista de departamentos para o filtro
  const [departments, setDepartments] = useState<Department[]>([]);
  // Estado para filtro de inbox (caixa de entrada) com persistência local
  const [inboxFilter, setInboxFilter] = useState<string | 'all'>(() => {
    const saved = localStorage.getItem('chat_inboxFilter');
    return saved || 'all';
  });
  
  // ===== ESTADOS DOS FILTROS AVANÇADOS =====
  // Lista de agentes para filtro avançado
  const [agents, setAgents] = useState<Array<{
    id: string;
    full_name: string | null;
    avatar_url: string | null;
  }>>([]);
  const [agentFilter, setAgentFilter] = useState<string | 'all'>('all');
  
  // Lista de tags para filtro avançado
  const [tags, setTags] = useState<Array<{
    id: string;
    name: string;
    color: string;
  }>>([]);
  const [tagFilter, setTagFilter] = useState<string | 'all'>('all');
  
  // Lista de fases kanban para filtro avançado
  const [phases, setPhases] = useState<Array<{
    id: string;
    name: string;
    color: string;
    category_id: string | null;
  }>>([]);
  const [phaseFilter, setPhaseFilter] = useState<string | 'all'>('all');
  
  // Lista de categorias kanban para filtro avançado
  const [categories, setCategories] = useState<Array<{
    id: string;
    name: string;
    color: string;
  }>>([]);
  const [categoryFilter, setCategoryFilter] = useState<string | 'all'>('all');
  // ===== FIM DOS FILTROS AVANÇADOS =====
  
  // Estados de paginação de mensagens
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);

  // ----- Jump-to-message / Anchor window (pesquisa contextual) -----
  // Janela ancorada substitui a lista quando o usuário pula para uma
  // mensagem antiga via pesquisa. Mantém custo constante (~51 msgs).
  const [hasMoreNewer, setHasMoreNewer] = useState(false);
  const [loadingMoreNewer, setLoadingMoreNewer] = useState(false);
  const [isAnchoredMode, setIsAnchoredMode] = useState(false);
  const [newMessagesWhileAnchored, setNewMessagesWhileAnchored] = useState(0);
  const [jumpingToMessageId, setJumpingToMessageId] = useState<string | null>(null);

  // Contagem total de mensagens da conversa selecionada
  const [totalMessagesCount, setTotalMessagesCount] = useState<number | null>(null);

  // Constantes de paginação
  // Reduzidos de 30 para 10 para acelerar a abertura da conversa (load more segue disponível)
  const MESSAGES_PAGE_SIZE = 10;
  const MESSAGES_INITIAL_LOAD = 10;
  
  // ===== REFS PARA OTIMIZAÇÃO DE PERFORMANCE (DEBOUNCE/CANCELAMENTO) =====
  // Timeout para debounce na seleção de conversa (evita múltiplas queries em cliques rápidos)
  const conversationLoadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Timeout para debounce no markAsRead (só marca após 1s de permanência na conversa)
   const markAsReadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
   // Ref para controle de duplicatas do markWhatsAppAsRead (evita flood de chamadas read_message)
   const lastMarkedMessageIdRef = useRef<string | null>(null);
   // Ref para debounce do markWhatsAppAsRead (agrupa mudanças rápidas em uma única chamada)
   const markWhatsAppAsReadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
   // ID da conversa sendo carregada (para descartar resultados obsoletos)
   const loadingConversationIdRef = useRef<string | null>(null);
  
  // ===== PERSISTÊNCIA DE FILTROS RÁPIDOS NO LOCALSTORAGE =====
  // Persiste todos os filtros rápidos quando qualquer um mudar
  useEffect(() => {
    const filters = {
      statusFilters,
      conversationTypeFilter,
      readStatusFilter,
      departmentFilter,
      scopeFilter
    };
    localStorage.setItem(CHAT_FILTERS_KEY, JSON.stringify(filters));
    chatLogger.debug('Filtros rápidos persistidos no cache', filters);
  }, [statusFilters, conversationTypeFilter, readStatusFilter, departmentFilter, scopeFilter]);
  
  // Persiste seleção de inbox separadamente (mantém compatibilidade)
  useEffect(() => {
    localStorage.setItem('chat_inboxFilter', inboxFilter);
  }, [inboxFilter]);
  // Lista de inboxes do tenant para o seletor
  // Estado das inboxes com status para indicador visual de conexão
  const [inboxes, setInboxes] = useState<Array<{
    id: string;
    name: string;
    channel_type: string;
    identifier: string;
    status?: string;
  }>>([]);

  // Flag para indicar que a lista de inboxes já foi carregada (sucesso ou erro)
  const [inboxesLoaded, setInboxesLoaded] = useState(false);
  
  // ===== INBOXES RESTRITAS (mantido para uso no ChatArea) =====
  // IDs das inboxes com access_restricted=true que o agente atual pode acessar
  const [restrictedInboxIds, setRestrictedInboxIds] = useState<string[]>([]);

  // ===== HOOK DE LISTAGEM DE CONVERSAS (SERVER-SIDE) =====
  // Substitui fetchConversations + filteredConversations + realtime subscription manual
  const conversationListFilters: ConversationListFilters = {
    statusFilters,
    inboxId: inboxFilter !== 'all' ? inboxFilter : null,
    departmentId: departmentFilter !== 'all' ? departmentFilter : null,
    agentId: agentFilter !== 'all' ? agentFilter : null,
    tagId: tagFilter !== 'all' ? tagFilter : null,
    phaseId: phaseFilter !== 'all' ? phaseFilter : null,
    categoryId: categoryFilter !== 'all' ? categoryFilter : null,
    readStatus: readStatusFilter !== 'all' ? readStatusFilter : null,
    conversationType: conversationTypeFilter !== 'all' ? conversationTypeFilter : null,
    search: debouncedSearch || null,
    // v1.1.0 - Escopo rápido (Meus/Todos/Bot)
    scope: scopeFilter,
  };
  const {
    conversations,
    totalCount,
    isLoading: conversationsLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    invalidateList,
    // v1.2.0: ações de fixar/desfixar conversa (persistido em conversation_pins)
    pinConversation,
    unpinConversation,
    isPinning,
  } = useConversationList(conversationListFilters, selectedConversation?.id);

  // Loading combinado: dados auxiliares + lista de conversas
  const loading = auxiliaryLoading || conversationsLoading;
  
  // ===== MODO OBSERVADOR =====
  // Permite supervisores/admins visualizarem conversas SEM marcar como lidas
  // Quando observer_mode_enabled=true no tenant_members, modo já vem ATIVO por padrão
  // Toggle permite desativar temporariamente para marcar como lida quando necessário
  const [canUseObserverMode, setCanUseObserverMode] = useState(false);
  const [isObserverModeActive, setIsObserverModeActive] = useState(false);
  // Estado para toggle de pausa do bot (Human Takeover)
  const [isTogglingBotPause, setIsTogglingBotPause] = useState(false);
  // Ref para evitar stale closure em callbacks realtime
  const isObserverModeActiveRef = useRef(false);
  useEffect(() => {
    isObserverModeActiveRef.current = isObserverModeActive;
  }, [isObserverModeActive]);

  // Ref espelho do modo ancorado para uso dentro do handler de realtime
  // (evita recriar a subscription quando o estado muda)
  const isAnchoredModeRef = useRef(false);
  useEffect(() => {
    isAnchoredModeRef.current = isAnchoredMode;
  }, [isAnchoredMode]);

  /**
   * Garante que o filtro de inbox nunca fique inválido.
   * 
   * Cenários cobertos:
   * - Troca de tenant: localStorage pode ter um inbox_id de outro tenant
   * - Inbox removida/inativada: valor salvo deixa de existir
   * - Valor vazio: evita o Select renderizar em branco
   */
  useEffect(() => {
    if (inboxesLoaded) {
      if (!inboxFilter) {
        setInboxFilter('all');
      } else if (inboxFilter !== 'all') {
        const exists = inboxes.some((i) => i.id === inboxFilter);

        if (!exists) {
          chatLogger.warn('Filtro de inbox inválido; resetando para "all"', {
            inboxFilter,
            inboxesCount: inboxes.length,
          });
          setInboxFilter('all');
        }
      }
    }
  }, [inboxFilter, inboxes, inboxesLoaded]);

  // Departamentos do agente logado (mantido para fetchUserDepartments - usado em UI)
  const [userDepartmentIds, setUserDepartmentIds] = useState<string[]>([]);
  // Estado para modal de seleção de template (janela 24h)
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Refs para manter userRole e userId atualizados nos callbacks (evita closure com valor antigo)
  const userRoleRef = useRef(userRole);
  const userIdRef = useRef(user?.id);
  const userDepartmentIdsRef = useRef<string[]>([]);
  // v3.0.0: refs auxiliares para mesclar restritas (RPC inboxes) + view_all (RPC sidebar)
  const restrictedBaseIdsRef = useRef<string[]>([]);
  const viewAllInboxIdsRef = useRef<string[]>([]);
  useEffect(() => {
    userRoleRef.current = userRole;
    userIdRef.current = user?.id;
    userDepartmentIdsRef.current = userDepartmentIds;
  }, [userRole, user?.id, userDepartmentIds]);
  
  // Mantém ref de selectedConversation sincronizada para uso em fetchConversations (evita stale closure)
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  // ===== EFEITO PARA RESETAR MOBILE VIEW QUANDO CONVERSA É DESSELECIONADA =====
  // Garante que em mobile, ao limpar seleção, volta para a lista
  useEffect(() => {
    if (!selectedConversation && isMobile) {
      setMobileView('list');
    }
  }, [selectedConversation, isMobile]);

  /**
   * Handler para selecionar conversa (usado pela ConversationList)
   * Em mobile, muda para a view de chat automaticamente
   */
  const handleSelectConversation = useCallback((conv: Conversation) => {
    setSelectedConversation(conv);
    if (isMobile) {
      setMobileView('chat');
    }
  }, [isMobile]);

  /**
   * Handler para voltar à lista de conversas (mobile)
   * Chamado pelo botão de voltar no ChatArea
   */
  const handleBackToList = useCallback(() => {
    setMobileView('list');
  }, []);

  // Contact presence (typing/recording indicator)
  const contactPresence = useContactPresence(
    selectedConversation?.inbox_id ?? null,
    selectedConversation?.contact?.phone ?? null,
    selectedConversation?.inbox?.provider_slug
  );
  
  // Hook para janela de 24h do WhatsApp Oficial
  const sessionWindow = useSessionWindow(
    selectedConversation?.inbox?.channel_type,
    selectedConversation?.last_inbound_at
  );
  
  // ===== HOOK PARA TRANSFERÊNCIA PENDENTE =====
  // Gerencia o fluxo de "Iniciar" atendimento para conversas transferidas
  const {
    hasPendingTransfer,
    acceptTransferAndStart,
    accepting: isStartingConversation,
  } = usePendingTransfer(selectedConversation?.id || null);
  
  /**
   * Handler para iniciar atendimento (aceitar transferência)
   * Move conversa de "Transferências" para "Em Atendimento"
   */
  const handleStartConversation = useCallback(async () => {
    const success = await acceptTransferAndStart();
    if (success) {
      toast.success('Atendimento iniciado!');
      // Atualiza estado local da conversa para refletir mudança
      setSelectedConversation(prev => prev ? {
        ...prev,
        status: 'open',
      } : null);
    }
  }, [acceptTransferAndStart]);
  
  
  // Send typing indicator when user starts typing
  const handleTyping = useCallback(() => {
    if (!selectedConversation?.contact?.phone || !selectedConversation?.inbox_id) return;
    
    // Debounce: only send every 3 seconds
    if (typingTimeoutRef.current) return;
    
    sendTypingIndicator(selectedConversation.inbox_id, selectedConversation.contact.phone, {
      type: 'composing',
      duration: 3000,
    });
    
    typingTimeoutRef.current = setTimeout(() => {
      typingTimeoutRef.current = null;
    }, 3000);
  }, [selectedConversation, sendTypingIndicator]);

  // REMOVIDO: useCallback markWhatsAppAsRead - lógica agora inlined no useEffect com debounce
  // para evitar flood de chamadas read_message ao provider-proxy (ver useEffect abaixo)

  /**
   * Callback para pausar/reativar bot na conversa (Human Takeover)
   * Usado tanto no ChatArea quanto no ContactSidebar
   */
  const handleToggleBotPause = useCallback(async () => {
    if (!selectedConversation) return;
    setIsTogglingBotPause(true);
    try {
      const newPausedState = !selectedConversation.bot_paused;
      // RPC server-side valida tenant + ownership da conversa
      const { error } = await supabase.rpc('set_conversation_bot_paused', {
        p_conversation_id: selectedConversation.id,
        p_paused: newPausedState,
        p_tenant_hint: currentTenant?.id,
      });
      if (error) throw error;
      
      // Atualiza estado local
      setSelectedConversation(prev => prev ? { ...prev, bot_paused: newPausedState } : null);
      toast.success(newPausedState ? 'Bot pausado nesta conversa' : 'Bot reativado nesta conversa');
    } catch (error) {
      console.error('Erro ao alternar pausa do bot:', error);
      toast.error('Erro ao alterar status do bot');
    } finally {
      setIsTogglingBotPause(false);
    }
  }, [selectedConversation, user?.id]);

  // IMPORTANTE: Para agentes, fetchInboxes e fetchUserDepartments DEVEM completar ANTES de fetchConversations
  // para que as refs restrictedInboxIdsRef e userDepartmentIdsRef estejam preenchidas corretamente.
  // Para supervisores, fetchSupervisorScope DEVE completar antes para ter escopo de supervisão.
  // Isso evita race condition onde conversas são filtradas com arrays vazios.
  useEffect(() => {
    if (!currentTenant?.id || permissionsLoading || userRole === null) return;
    
    // Função assíncrona para carregar dados auxiliares (filtros, UI)
    // A listagem de conversas agora é gerenciada pelo hook useConversationList
    const initChatLoad = async () => {
      chatLogger.debug('Iniciando carga do chat (dados auxiliares)', { userRole, userId: user?.id });
      
      // Carrega todos os dados de UI em paralelo
      // v3.0.0: fetchChatSidebarOptions consolida departamentos + view_all_inbox_ids
      fetchChatSidebarOptions();
      // v2.0.0: RPC consolidada substitui fetchAgents/fetchTags/fetchPhases/fetchCategories
      fetchChatFilterOptions();
      fetchObserverModeConfig();
      
      // Aguarda inboxes (necessário para filtro de UI e restrictedInboxIds do ChatArea)
      await fetchInboxes();
      
      // Marca dados auxiliares como carregados
      setAuxiliaryLoading(false);
    };
    
    initChatLoad();
  }, [currentTenant?.id, userRole, permissionsLoading]);

  // Busca departamentos vinculados ao agente logado
  const fetchUserDepartments = async () => {
    if (!user?.id || !currentTenant?.id) return;
    
    try {
      // RPC server-side: lista departamentos do usuário no tenant validado
      const { data, error } = await supabase.rpc('get_user_department_ids', {
        p_tenant_hint: currentTenant.id,
      });

      if (error) throw error;
      const ids = (data || []).map((d: { department_id: string }) => d.department_id);
      setUserDepartmentIds(ids);
      userDepartmentIdsRef.current = ids;
      chatLogger.debug('Departamentos do agente carregados', { userId: user.id, departments: ids });
    } catch (error) {
      chatLogger.error('Erro ao carregar departamentos do agente', error);
    }
  };
  
  // fetchSupervisorScope REMOVIDO - RBAC agora é server-side via RPC get_conversations_list
  
  /**
   * Busca configuração do Modo Observador do usuário logado
   * Se observer_mode_enabled=true no tenant_members, ativa modo por padrão
   * Supervisores/Admins usam para monitorar conversas sem marcar como lidas
   */
  const fetchObserverModeConfig = async () => {
    if (!user?.id || !currentTenant?.id) return;
    
    try {
      // RPC server-side: retorna flag observer_mode_enabled do usuário no tenant
      const { data, error } = await supabase.rpc('get_user_observer_mode', {
        p_tenant_hint: currentTenant.id,
      });

      if (error) throw error;

      const enabled = Boolean(data);
      setCanUseObserverMode(enabled);
      // Se habilitado no banco, modo observador já vem ATIVO por padrão
      setIsObserverModeActive(enabled);
      isObserverModeActiveRef.current = enabled;
      
      chatLogger.debug('Modo Observador configurado', { 
        canUse: enabled, 
        active: enabled 
      });
    } catch (error) {
      chatLogger.error('Erro ao carregar config do Modo Observador', error);
    }
  };
  
  // ===== FETCH DE INBOXES (v3.0.0) =====
  // Usa get_user_accessible_inboxes ENRIQUECIDA — retorna detalhes completos
  // (name, channel_type, identifier, status, access_restricted) em uma única chamada,
  // eliminando o SELECT direto adicional em `inboxes` que existia antes.
  const fetchInboxes = async () => {
    if (!currentTenant?.id || !user?.id) return;

    setInboxesLoaded(false);

    try {
      const { data, error } = await supabase
        .rpc('get_user_accessible_inboxes', {
          _user_id: user.id,
          _tenant_id: currentTenant.id,
        });

      if (error) {
        chatLogger.error('Erro ao buscar inboxes acessíveis via RPC', error);
        setInboxes([]);
        return;
      }

      const accessible = (data as Array<{
        id: string;
        name: string;
        channel_type: string;
        identifier: string;
        status: string;
        access_restricted: boolean;
      }> | null) || [];

      setInboxes(accessible);

      // Inboxes restritas que o agente tem acesso → visibilidade total dessas conversas
      const restrictedIds = accessible
        .filter(i => i.access_restricted === true)
        .map(i => i.id);

      restrictedBaseIdsRef.current = restrictedIds;
      // Mescla com view_all_inbox_ids carregado por fetchChatSidebarOptions
      const viewAll = viewAllInboxIdsRef.current || [];
      setRestrictedInboxIds(Array.from(new Set([...restrictedIds, ...viewAll])));

      chatLogger.debug('Inboxes carregadas via RPC enriquecida', {
        count: accessible.length,
        restrictedCount: restrictedIds.length,
      });
    } catch (error) {
      chatLogger.error('Erro ao carregar inboxes', error);
    } finally {
      setInboxesLoaded(true);
    }
  };

  // ===== FETCH CONSOLIDADO PARA SIDEBAR (v1.0.0) =====
  // Substitui fetchDepartments + SELECT direto em inbox_agents.
  // Retorna { departments, view_all_inbox_ids } em uma única RPC server-side.
  const fetchChatSidebarOptions = async () => {
    if (!currentTenant?.id) return;

    try {
      const { data, error } = await supabase.rpc('get_chat_sidebar_options', {
        p_tenant_hint: currentTenant.id,
      });

      if (error) {
        chatLogger.error('Erro ao buscar opções do sidebar via RPC', error);
        return;
      }

      const payload = (data as any) || {};
      const departmentsList = payload.departments || [];
      const viewAllIds: string[] = payload.view_all_inbox_ids || [];

      setDepartments(departmentsList);
      viewAllInboxIdsRef.current = viewAllIds;

      // Mescla com restritas já carregadas (se já vieram)
      const base = restrictedBaseIdsRef.current;
      setRestrictedInboxIds(Array.from(new Set([...base, ...viewAllIds])));

      chatLogger.debug('Opções do sidebar carregadas', {
        departments: departmentsList.length,
        viewAllInboxIds: viewAllIds.length,
      });
    } catch (error) {
      chatLogger.error('Erro ao carregar opções do sidebar', error);
    }
  };

  // ===== FETCH CONSOLIDADO PARA FILTROS AVANÇADOS =====
  // v2.0.0 — Migração para RPC server-side `get_chat_filter_options`
  // Substitui 4 SELECTs diretos (tenant_members, contact_tags, kanban_phases, kanban_categories)
  // por 1 única chamada RPC com agregação no servidor (segurança + performance)
  const fetchChatFilterOptions = async () => {
    if (!currentTenant?.id) return;

    try {
      const { data, error } = await supabase.rpc('get_chat_filter_options', {
        p_tenant_hint: currentTenant.id,
      });

      if (error) throw error;

      // Payload: { agents, tags, phases, categories }
      const payload = (data as any) || {};

      setAgents(payload.agents || []);
      setTags(payload.tags || []);
      setPhases(payload.phases || []);
      setCategories(payload.categories || []);

      chatLogger.debug('Opções de filtro do chat carregadas via RPC', {
        agents: (payload.agents || []).length,
        tags: (payload.tags || []).length,
        phases: (payload.phases || []).length,
        categories: (payload.categories || []).length,
      });
    } catch (error) {
      chatLogger.error('Erro ao carregar opções de filtro do chat (RPC)', error);
    }
  };
  // ===== FIM DAS FUNÇÕES DE FILTROS AVANÇADOS =====

  // Processa dados de navegação vindos da página de Contatos
  useEffect(() => {
    const state = location.state as NavigationState | null;
    
    if (state?.contactId && state?.inboxId && currentTenant?.id && !auxiliaryLoading) {
      handleInitialContact(state);
      // Limpa o state da URL para evitar reprocessamento
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, currentTenant?.id, auxiliaryLoading]);

  // Função para processar contato inicial vindo de navegação
  const handleInitialContact = async (state: NavigationState) => {
    if (!state.contactId || !state.inboxId) return;
    
    try {
      // 1. Criar ou obter a conversa existente
      const { data: conversationId, error: rpcError } = await supabase.rpc('get_or_create_conversation', {
        _inbox_id: state.inboxId,
        _contact_id: state.contactId,
      });

      if (rpcError) throw rpcError;

      // 2. Invalida cache para incluir nova conversa na lista
      invalidateList();

      // 3. Busca a conversa completa para selecionar
      const { data: convData, error: convError } = await supabase
        .from('conversations')
        .select(`
          id,
          inbox_id,
          contact_id,
          assigned_to,
          department_id,
          status,
          last_message_at,
          last_message_preview,
          unread_count,
          created_at,
          contacts (
            id,
            name,
            phone,
            email,
            avatar_url,
            is_saved_to_agenda,
            metadata
          ),
          inboxes (
            id,
            name,
            channel_type,
            identifier,
            settings,
            api_providers:provider_id (
              slug
            )
          ),
          departments (
            id,
            name
          ),
          profiles:assigned_to (
            id,
            full_name,
            avatar_url
          )
        `)
        .eq('id', conversationId)
        .single();

      if (convError) throw convError;

      // 4. Formata e seleciona a conversa
      const formattedConversation: Conversation = {
        id: convData.id,
        inbox_id: convData.inbox_id,
        contact_id: convData.contact_id,
        contact: {
          id: convData.contacts?.id || '',
          name: convData.contacts?.name || null,
          phone: convData.contacts?.phone || null,
          email: convData.contacts?.email || null,
          avatar_url: convData.contacts?.avatar_url || null,
          is_saved_to_agenda: convData.contacts?.is_saved_to_agenda || false,
          metadata: (convData.contacts?.metadata as Record<string, unknown>) || null,
          tags: [], // Tags serão carregadas pelo fetchConversations
        },
        group_id: null, // Conversas iniciadas assim são sempre individuais
        group: null,
        inbox: {
          id: convData.inboxes?.id || '',
          name: convData.inboxes?.name || '',
          channel_type: convData.inboxes?.channel_type || '',
          identifier: convData.inboxes?.identifier || '',
          settings: convData.inboxes?.settings as Conversation['inbox']['settings'],
          provider_slug: (convData.inboxes as any)?.api_providers?.slug || null,
        },
        department_id: convData.department_id,
        department: convData.departments ? {
          id: convData.departments.id,
          name: convData.departments.name,
        } : null,
        assigned_to: convData.assigned_to,
        // Acessa dados do agente via propriedade 'profiles' retornada pelo Supabase
        assigned_agent: (convData as any).profiles ? {
          id: (convData as any).profiles.id,
          full_name: (convData as any).profiles.full_name,
          avatar_url: (convData as any).profiles.avatar_url,
        } : null,
        status: convData.status,
        last_message_at: convData.last_message_at,
        last_message_preview: convData.last_message_preview,
        unread_count: 0, // Acabamos de abrir, então marcar como lido
        created_at: convData.created_at,
        // Human Takeover: estado de pausa do bot (não disponível nesta query simplificada)
        bot_paused: false,
      };

      setSelectedConversation(formattedConversation);
      // No mobile, navegar automaticamente para a tela de chat após iniciar conversa
      if (isMobile) {
        setMobileView('chat');
      }
      toast.success(`Conversa com ${state.contactName || 'contato'} iniciada`);
    } catch (error) {
      console.error('Erro ao iniciar conversa:', error);
      toast.error('Erro ao iniciar conversa com o contato');
    }
  };

  // ===== PWA RESILIENCE: Auto-refresh quando app volta do background =====
  // Agora usa invalidateList do hook useConversationList
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && currentTenant?.id) {
        invalidateList();
        chatLogger.info('[PWA] App voltou ao foreground, invalidando lista de conversas');
      }
    };

    const handleOnline = () => {
      if (currentTenant?.id) {
        chatLogger.info('[PWA] Conexão restaurada, invalidando lista de conversas');
        invalidateList();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [currentTenant?.id, invalidateList]);
  // ===== FIM PWA RESILIENCE =====

  // ===== REFRESH AO MONTAR A PÁGINA DO CHAT (v1.3.1) =====
  // Quando o usuário sai do Chat (ex: vai pro Kanban) e volta, o hook
  // useConversationList desmonta e perde os eventos Realtime que chegam
  // nesse intervalo. Ao remontar, força um invalidate único para garantir
  // que a lista reflita o estado atual mesmo se o cache estiver "fresh".
  useEffect(() => {
    if (currentTenant?.id) {
      invalidateList();
      chatLogger.debug('[Mount] Página do Chat montada — invalidando lista de conversas');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenant?.id]);
  // ===== FIM REFRESH AO MONTAR =====


  // ===== EFEITO PARA CARREGAR CONVERSA =====
  // Implementa debounce de 300ms para evitar múltiplas execuções em cliques rápidos
  // NÃO depende de isObserverModeActive para evitar recarregamento ao toggle do modo observador
  useEffect(() => {
    // Limpa timeout anterior se existir (cancela operação pendente)
    if (conversationLoadTimeoutRef.current) {
      clearTimeout(conversationLoadTimeoutRef.current);
      conversationLoadTimeoutRef.current = null;
    }

    if (!selectedConversation) {
      setTotalMessagesCount(null);
      loadingConversationIdRef.current = null;
      return;
    }
    
    // Guarda ID da conversa atual para validação posterior
    const conversationId = selectedConversation.id;
    loadingConversationIdRef.current = conversationId;
    // Reseta controle de duplicatas do markWhatsAppAsRead ao mudar de conversa
    lastMarkedMessageIdRef.current = null;

    // Debounce de 300ms antes de executar operações pesadas
    conversationLoadTimeoutRef.current = setTimeout(() => {
      // Verifica se ainda é a mesma conversa (evita race condition)
      if (loadingConversationIdRef.current !== conversationId) {
        chatLogger.debug('[Debounce] Conversa mudou durante debounce, descartando', { 
          expected: conversationId.slice(0, 8),
          current: loadingConversationIdRef.current?.slice(0, 8) 
        });
        return;
      }
      
      fetchMessages(conversationId, true); // Initial load for this conversation
      fetchMessageCount(conversationId); // Busca contagem total
    }, 300);

    // Cleanup: cancela timeout pendente ao desmontar ou mudar conversa
    return () => {
      if (conversationLoadTimeoutRef.current) {
        clearTimeout(conversationLoadTimeoutRef.current);
        conversationLoadTimeoutRef.current = null;
      }
    };
  }, [selectedConversation?.id]); // REMOVIDO isObserverModeActive para evitar reload ao toggle

  // ===== EFEITO PARA MARCAR COMO LIDA (MODO OBSERVADOR) =====
  // Separado do carregamento para evitar recarregar mensagens ao toggle do modo observador
  // Só marca como lida após 1 segundo de permanência na conversa (lazy mark as read)
  useEffect(() => {
    // Limpa timeout anterior se existir
    if (markAsReadTimeoutRef.current) {
      clearTimeout(markAsReadTimeoutRef.current);
      markAsReadTimeoutRef.current = null;
    }

    if (!selectedConversation) return;
    
    const conversationId = selectedConversation.id;
    
    // Só marca como lida se NÃO estiver em modo observador
    if (!isObserverModeActive) {
      markAsReadTimeoutRef.current = setTimeout(() => {
        // Verifica se ainda é a mesma conversa
        if (loadingConversationIdRef.current !== conversationId) {
          return;
        }
        markAsRead(conversationId);
      }, 1000);
    }

    // Cleanup: cancela timeout pendente ao desmontar ou mudar conversa/modo
    return () => {
      if (markAsReadTimeoutRef.current) {
        clearTimeout(markAsReadTimeoutRef.current);
        markAsReadTimeoutRef.current = null;
      }
    };
  }, [selectedConversation?.id, isObserverModeActive]);

  // Mark WhatsApp messages as read when viewing conversation (COM DEBOUNCE)
  // Evita flood de chamadas read_message ao provider-proxy que causava LockManager timeout
  useEffect(() => {
    // Limpa timeout anterior para agrupar mudanças rápidas
    if (markWhatsAppAsReadTimeoutRef.current) {
      clearTimeout(markWhatsAppAsReadTimeoutRef.current);
      markWhatsAppAsReadTimeoutRef.current = null;
    }

    if (messages.length === 0 || !selectedConversation?.inbox_id || !selectedConversation?.contact?.phone) return;
    
    // Se está em modo observador, não marca como lida
    if (isObserverModeActive) return;

    // Encontra última mensagem inbound não lida
    const unreadMessages = messages.filter(
      m => m.direction === 'inbound' && m.external_id && !m.read_at
    );
    const lastMessage = unreadMessages[unreadMessages.length - 1];
    
    if (!lastMessage?.external_id) return;
    
    // Se já marcou esta mensagem, não repete (deduplicação)
    if (lastMarkedMessageIdRef.current === lastMessage.external_id) return;

    // Debounce de 2 segundos para agrupar mudanças rápidas em uma única chamada
    markWhatsAppAsReadTimeoutRef.current = setTimeout(() => {
      lastMarkedMessageIdRef.current = lastMessage.external_id;
      markMessageAsRead(
        selectedConversation.inbox_id,
        selectedConversation.contact!.phone,
        lastMessage.external_id
      );
    }, 2000);

    return () => {
      if (markWhatsAppAsReadTimeoutRef.current) {
        clearTimeout(markWhatsAppAsReadTimeoutRef.current);
      }
    };
  }, [messages, selectedConversation?.id, isObserverModeActive, markMessageAsRead]);

  // ===== REALTIME SUBSCRIPTION OTIMIZADA PARA MENSAGENS =====
  // Implementa debounce de 300ms para evitar múltiplos ciclos connect/disconnect
  // ao navegar rapidamente entre conversas
  useEffect(() => {
    if (!selectedConversation) return;
    
    const conversationId = selectedConversation.id;
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let subscriptionTimeout: NodeJS.Timeout | null = null;
    
    // Debounce para evitar múltiplas subscriptions em cliques rápidos
    subscriptionTimeout = setTimeout(() => {
      // Verifica se ainda é a mesma conversa antes de criar subscription
      if (loadingConversationIdRef.current !== conversationId) {
        chatLogger.debug('[Realtime] Conversa mudou durante debounce de subscription', {
          expected: conversationId.slice(0, 8),
          current: loadingConversationIdRef.current?.slice(0, 8)
        });
        return;
      }
      
      channel = supabase
        .channel(`messages-${conversationId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'messages',
            filter: `conversation_id=eq.${conversationId}`,
          },
          async (payload) => {
            // Verifica se ainda é a conversa ativa antes de processar
            if (loadingConversationIdRef.current !== conversationId) {
              chatLogger.debug('[Realtime] INSERT ignorado - conversa mudou', {
                messageConv: conversationId.slice(0, 8),
                current: loadingConversationIdRef.current?.slice(0, 8)
              });
              return;
            }
            
            const newMessage = payload.new as Message;
            
            // SERVER-SIDE: hidrata mensagem completa via RPC (attachments + reply_to + sender)
            const { data: fullRows, error: fullErr } = await supabase.rpc('get_message_full_by_id', {
              p_message_id: newMessage.id,
              p_tenant_hint: currentTenant?.id ?? null,
            });

            if (fullErr) {
              chatLogger.warn('RPC get_message_full_by_id falhou no realtime INSERT', {
                error: fullErr.message,
                messageId: newMessage.id,
              });
            }

            const full = fullRows && fullRows.length > 0 ? (fullRows[0] as any) : null;

            const attachmentsArr = ((full?.attachments as any[]) || []).map((att: any) => ({
              id: att.id,
              type: att.type,
              url: att.url,
              mime_type: att.mime_type,
              filename: att.filename,
              size_bytes: att.size_bytes,
              metadata: (att.metadata ?? null) as Record<string, unknown> | null,
            }));

            const replyToData = full?.reply_to
              ? {
                  id: full.reply_to.id,
                  content: full.reply_to.content,
                  message_type: full.reply_to.message_type,
                  sender_type: full.reply_to.sender_type,
                  attachments: (full.reply_to.attachments || []).map((att: any) => ({
                    ...att,
                    metadata: null,
                  })),
                }
              : null;

            // Sender: prioriza fallback local (sender_id === user.id) para evitar lookup
            let senderData: { id: string; full_name: string | null; avatar_url: string | null } | null = null;
            if (newMessage.sender_type === 'agent' && newMessage.sender_id) {
              if (newMessage.sender_id === user?.id && profile) {
                senderData = {
                  id: user.id,
                  full_name: profile.full_name || null,
                  avatar_url: profile.avatar_url || null,
                };
              } else if (full?.sender) {
                senderData = {
                  id: full.sender.id,
                  full_name: full.sender.full_name,
                  avatar_url: full.sender.avatar_url,
                };
              }
            }

            // Verifica novamente se conversa ainda é a mesma (pode ter mudado durante queries)
            if (loadingConversationIdRef.current !== conversationId) return;

            const messageWithAttachments: Message = {
              ...newMessage,
              attachments: attachmentsArr,
              reply_to: replyToData,
              sender: senderData,
            };
            
            // Em modo ancorado (jump-to-message via pesquisa) NÃO fazemos
            // append direto — apenas contabilizamos para mostrar a pílula
            // "Ir para o fim". Mensagens próprias outbound continuam visíveis
            // para feedback imediato após envio.
            const isOwnOutbound =
              newMessage.direction === 'outbound' &&
              newMessage.sender_type === 'agent' &&
              newMessage.sender_id === user?.id;

            if (isAnchoredModeRef.current && !isOwnOutbound) {
              setNewMessagesWhileAnchored(prev => prev + 1);
            } else {
              setMessages(prev => {
                // Avoid duplicates
                if (prev.some(m => m.id === newMessage.id)) return prev;
                return [...prev, messageWithAttachments];
              });
            }
            
            // Se mensagem inbound chegou na conversa aberta:
            // 1. Marca como lida imediatamente (se não estiver no modo observador)
            // 2. Atualiza last_inbound_at para recalcular janela 24h (WhatsApp Oficial)
            if (newMessage.direction === 'inbound') {
              // ===== MODO OBSERVADOR =====
              // Usa ref para evitar stale closure no callback do realtime
              if (!isObserverModeActiveRef.current) {
                markAsRead(conversationId);
              }
              
              // Atualiza last_inbound_at imediatamente para que:
              // - O alerta "Janela 24h expirada" desapareça instantaneamente
              // - A seção Estatísticas mostre tempo restante correto
              // - useSessionWindow recalcule isExpired = false
              // REGRA: Comentários do Instagram NÃO resetam a janela de 24h (apenas DMs/stories)
              // Usa função centralizada para evitar divergência com useChatConversation.ts
              const isCommentMsg = !isEligibleForSessionWindow(newMessage);
              
              if (!isCommentMsg) {
                setSelectedConversation(prev => 
                  prev ? { ...prev, last_inbound_at: newMessage.sent_at } : prev
                );
                
                chatLogger.debug('[Realtime] last_inbound_at atualizado via mensagem inbound (não-comentário)', {
                  conversationId: conversationId.slice(0, 8),
                  newLastInbound: newMessage.sent_at,
                  observerMode: isObserverModeActiveRef.current,
                });
              } else {
                chatLogger.debug('[Realtime] Comentário recebido — last_inbound_at NÃO atualizado (sem impacto na janela 24h)', {
                  conversationId: conversationId.slice(0, 8),
                  messageType: newMessage.message_type,
                });
              }
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'messages',
            filter: `conversation_id=eq.${conversationId}`,
          },
          (payload) => {
            // Verifica se ainda é a conversa ativa
            if (loadingConversationIdRef.current !== conversationId) return;
            
            const updatedMessage = payload.new as Message;
            // Atualiza mensagem preservando dados enriquecidos que não vêm no Realtime UPDATE
            setMessages(prev =>
              prev.map(m => m.id === updatedMessage.id 
                ? { 
                    ...m, 
                    ...updatedMessage,
                    // Preserva dados de relacionamento que não vêm no payload do Realtime
                    reply_to: m.reply_to,
                    attachments: m.attachments,
                    sender: m.sender, // Preserva avatar e dados do agente remetente
                  } 
                : m
              )
            );
          }
        )
        .subscribe();
    }, 300); // Mesmo debounce da seleção

    return () => {
      if (subscriptionTimeout) {
        clearTimeout(subscriptionTimeout);
      }
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [selectedConversation?.id, user?.id, profile]);

  // ===== HIDRATAÇÃO TARDIA DE SENDER =====
  // Quando auth fica disponível após carregamento inicial, re-busca dados de sender
  // que falharam por race condition (RPC chamado antes de auth pronta)
  useEffect(() => {
    // Só executa se temos user autenticado E mensagens carregadas
    if (!user?.id || messages.length === 0) return;
    
    // Encontra mensagens de agente que têm sender_id mas não têm sender (dados)
    const messagesWithMissingSender = messages.filter(
      m => m.sender_type === 'agent' && m.sender_id && !m.sender
    );
    
    // Se não há mensagens com sender faltando, não faz nada
    if (messagesWithMissingSender.length === 0) return;
    
    chatLogger.debug('Hidratação tardia: encontradas mensagens sem sender', {
      count: messagesWithMissingSender.length,
    });
    
    // Coleta sender_ids únicos que precisam ser hidratados
    const senderIdsToHydrate = [...new Set(
      messagesWithMissingSender.map(m => m.sender_id as string)
    )];
    
    // Função async para hidratar senders
    const hydrateSenders = async () => {
      try {
        const { data: senders, error: sendersError } = await supabase
          .rpc('get_profiles_public_info', { p_user_ids: senderIdsToHydrate });
        
        if (sendersError) {
          chatLogger.warn('Hidratação tardia falhou', { error: sendersError.message });
          return;
        }
        
        if (!senders || senders.length === 0) return;
        
        // Cria mapa para lookup rápido
        const sendersMap = new Map<string, { id: string; full_name: string | null; avatar_url: string | null }>();
        senders.forEach((s: { id: string; full_name: string; avatar_url: string | null }) => {
          sendersMap.set(s.id, {
            id: s.id,
            full_name: s.full_name,
            avatar_url: s.avatar_url,
          });
        });
        
        // Aplica patch no state - preserva tudo exceto sender
        setMessages(prev => prev.map(m => {
          // Só atualiza mensagens que precisam de hidratação
          if (m.sender_type === 'agent' && m.sender_id && !m.sender) {
            const senderData = sendersMap.get(m.sender_id);
            if (senderData) {
              return { ...m, sender: senderData };
            }
          }
          return m;
        }));
        
        chatLogger.debug('Hidratação tardia concluída', {
          senderIds: senderIdsToHydrate.length,
          found: senders.length,
        });
      } catch (error) {
        chatLogger.error('Erro na hidratação tardia de senders', error);
      }
    };
    
    hydrateSenders();
  }, [user?.id, messages.length]); // Dispara quando auth fica pronta ou mensagens mudam

  // Realtime subscription para conversas REMOVIDA - agora gerenciado pelo hook useConversationList

  // ===== Realtime subscription para atualizações de contatos =====
  // Necessário para atualizar is_saved_to_agenda em tempo real quando
  // o contato é salvo automaticamente na agenda pelo webhook
  useEffect(() => {
    if (!selectedConversation?.contact?.id) return;

    const contactId = selectedConversation.contact.id;
    
    const channel = supabase
      .channel(`contact-updates-${contactId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'contacts',
          filter: `id=eq.${contactId}`,
        },
        (payload) => {
          const updatedContact = payload.new as {
            id: string;
            name: string | null;
            phone: string | null;
            email: string | null;
            avatar_url: string | null;
            is_saved_to_agenda: boolean | null;
          };
          
          chatLogger.debug('[Realtime] Contato atualizado:', {
            contactId,
            is_saved_to_agenda: updatedContact.is_saved_to_agenda,
          });
          
          // Atualiza o contato na conversa selecionada
          setSelectedConversation(prev => {
            if (!prev || !prev.contact) return prev;
            return {
              ...prev,
              contact: {
                ...prev.contact,
                name: updatedContact.name,
                phone: updatedContact.phone,
                email: updatedContact.email,
                avatar_url: updatedContact.avatar_url,
                is_saved_to_agenda: updatedContact.is_saved_to_agenda ?? false,
              },
            };
          });
          
          // Atualiza contato na lista via invalidação do cache do hook
          invalidateList();
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          chatLogger.debug('[Realtime] Canal de contato conectado:', contactId);
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedConversation?.contact?.id]);

  // fetchConversations REMOVIDO - substituído pelo hook useConversationList
  // A sincronização da selectedConversation agora é feita via efeito abaixo
  useEffect(() => {
    if (!selectedConversation || conversations.length === 0) return;
    const updated = conversations.find(c => c.id === selectedConversation.id);
    if (updated) {
      setSelectedConversation(prev => prev ? {
        ...prev,
        assigned_to: updated.assigned_to,
        assigned_agent: updated.assigned_agent,
        department_id: updated.department_id,
        department: updated.department,
        status: updated.status,
        unread_count: updated.unread_count,
        last_message_at: updated.last_message_at,
        last_message_preview: updated.last_message_preview,
        bot_paused: updated.bot_paused,
        bot_paused_at: updated.bot_paused_at,
        bot_paused_until: updated.bot_paused_until,
      } : prev);
    }
  }, [conversations]);

  // Busca mensagens da conversa com paginação (mais recentes primeiro, depois carrega antigas)
  const fetchMessages = async (conversationId: string, isInitialLoad = false, loadMore = false) => {
    try {
      if (loadMore) {
        setLoadingMoreMessages(true);
      } else if (isInitialLoad) {
        setMessagesLoading(true);
      }

      const pageSize = loadMore ? MESSAGES_PAGE_SIZE : MESSAGES_INITIAL_LOAD;

      // Cursor para load more: sent_at da mensagem mais antiga atualmente carregada
      const beforeSentAt = loadMore && messages.length > 0
        ? messages[0].sent_at
        : null;

      // SERVER-SIDE: uma única chamada RPC traz mensagens + attachments + reply_to + sender
      // Substitui SELECT direto em messages/message_attachments para reduzir carga no banco
      const { data, error } = await supabase.rpc('get_conversation_messages_page', {
        p_conversation_id: conversationId,
        p_before_sent_at: beforeSentAt,
        p_page_size: pageSize,
        p_tenant_hint: currentTenant?.id ?? null,
      });

      if (error) throw error;

      // RPC retorna DESC; reverte para ordem cronológica para exibição
      const orderedData = (data || []).slice().reverse();

      // Verifica se há mais mensagens para carregar
      setHasMoreMessages((data?.length || 0) === pageSize);

      // Formata as mensagens (RPC já entrega attachments/reply_to/sender prontos)
      const formattedMessages: Message[] = orderedData.map((msg: any) => ({
        id: msg.id,
        conversation_id: msg.conversation_id,
        sender_type: msg.sender_type,
        sender_id: msg.sender_id,
        content: msg.content,
        message_type: msg.message_type,
        direction: msg.direction,
        status: msg.status,
        sent_at: msg.sent_at,
        delivered_at: msg.delivered_at,
        read_at: msg.read_at,
        external_id: msg.external_id,
        reply_to_id: msg.reply_to_id,
        attachments: (msg.attachments || []).map((att: any) => ({
          ...att,
          metadata: (att.metadata ?? null) as Record<string, unknown> | null,
        })),
        reply_to: msg.reply_to
          ? {
              id: msg.reply_to.id,
              content: msg.reply_to.content,
              message_type: msg.reply_to.message_type as MessageType,
              sender_type: msg.reply_to.sender_type as SenderType,
              attachments: (msg.reply_to.attachments || []).map((att: any) => ({
                ...att,
                metadata: null,
              })),
            }
          : null,
        reactions: Array.isArray(msg.reactions) ? (msg.reactions as unknown as MessageReaction[]) : null,
        metadata: msg.metadata as Record<string, unknown> | null,
        deleted_at: msg.deleted_at,
        participant_phone: msg.participant_phone,
        sender: msg.sender
          ? {
              id: msg.sender.id,
              full_name: msg.sender.full_name,
              avatar_url: msg.sender.avatar_url,
            }
          : null,
      }));

      // Merge mensagens: prepend para load more, replace para carga inicial
      if (loadMore) {
        setMessages(prev => [...formattedMessages, ...prev]);
      } else {
        setMessages(formattedMessages);
        // Carga inicial: sai de qualquer modo ancorado anterior
        setIsAnchoredMode(false);
        setHasMoreNewer(false);
        setNewMessagesWhileAnchored(0);
      }
    } catch (error) {
      // LockManager timeout é transiente (contenção de auth lock) - não exibe toast
      const isLockTimeout = error instanceof Error && 
        error.message?.includes('LockManager');
      
      if (!isLockTimeout) {
        console.error('Error fetching messages:', error);
        toast.error('Erro ao carregar mensagens');
      } else {
        chatLogger.warn('LockManager timeout ao carregar mensagens - retry pendente');
      }
    } finally {
      setLoadingMoreMessages(false);
      if (isInitialLoad) setMessagesLoading(false);
    }
  };

  // Carrega mais mensagens antigas (paginação)
  const handleLoadMoreMessages = () => {
    if (!selectedConversation || loadingMoreMessages || !hasMoreMessages) return;
    fetchMessages(selectedConversation.id, false, true);
  };

  // ---------------------------------------------------------------------------
  // Jump-to-message / Anchor window — pesquisa contextual
  // ---------------------------------------------------------------------------
  // Mapeia uma linha bruta da RPC para o tipo Message (reaproveita formato
  // idêntico ao de fetchMessages — single source of truth).
  const mapRpcMessageRow = (msg: any): Message => ({
    id: msg.id,
    conversation_id: msg.conversation_id,
    sender_type: msg.sender_type,
    sender_id: msg.sender_id,
    content: msg.content,
    message_type: msg.message_type,
    direction: msg.direction,
    status: msg.status,
    sent_at: msg.sent_at,
    delivered_at: msg.delivered_at,
    read_at: msg.read_at,
    external_id: msg.external_id,
    reply_to_id: msg.reply_to_id,
    attachments: (msg.attachments || []).map((att: any) => ({
      ...att,
      metadata: (att.metadata ?? null) as Record<string, unknown> | null,
    })),
    reply_to: msg.reply_to
      ? {
          id: msg.reply_to.id,
          content: msg.reply_to.content,
          message_type: msg.reply_to.message_type as MessageType,
          sender_type: msg.reply_to.sender_type as SenderType,
          attachments: (msg.reply_to.attachments || []).map((att: any) => ({
            ...att,
            metadata: null,
          })),
        }
      : null,
    reactions: Array.isArray(msg.reactions) ? (msg.reactions as unknown as MessageReaction[]) : null,
    metadata: msg.metadata as Record<string, unknown> | null,
    deleted_at: msg.deleted_at,
    participant_phone: msg.participant_phone,
    sender: msg.sender
      ? {
          id: msg.sender.id,
          full_name: msg.sender.full_name,
          avatar_url: msg.sender.avatar_url,
        }
      : null,
  });

  // Salto até uma mensagem específica (pesquisa contextual): substitui a lista
  // por uma janela ancorada (~51 msgs) centrada no alvo. Custo constante
  // independente do tamanho da conversa.
  // - Guarda contra troca de conversa durante o await (anti-race).
  // - Erro real (RPC/PostgREST) é exposto no toast para facilitar diagnóstico.
  const handleJumpToMessage = async (messageId: string): Promise<boolean> => {
    if (!selectedConversation) {
      chatLogger.warn('[jumpToMessage] sem conversa selecionada');
      return false;
    }
    // Atalho: alvo já está na DOM
    if (messages.some(m => m.id === messageId)) return true;

    // Captura a conversa de origem para abortar se o usuário trocar enquanto a RPC roda
    const convAtStart = selectedConversation.id;
    chatLogger.info('[jumpToMessage] start', {
      conversationId: convAtStart,
      messageId,
      tenant: currentTenant?.id ?? null,
    });

    try {
      setJumpingToMessageId(messageId);
      chatLogger.info('[jumpToMessage] calling RPC get_conversation_messages_around');
      const { data, error } = await supabase.rpc('get_conversation_messages_around', {
        p_conversation_id: convAtStart,
        p_anchor_message_id: messageId,
        p_before_count: 25,
        p_after_count: 25,
        p_tenant_hint: currentTenant?.id ?? null,
      });

      // Aborta silenciosamente se a conversa mudou no meio do caminho
      if (!selectedConversation || selectedConversation.id !== convAtStart) {
        chatLogger.warn('[jumpToMessage] conversa trocou durante a RPC, descartando resultado');
        return false;
      }

      if (error) throw error;
      if (!data || data.length === 0) {
        chatLogger.warn('[jumpToMessage] RPC retornou vazio', { messageId });
        toast.error('Mensagem não encontrada nesta conversa.');
        return false;
      }
      chatLogger.info('[jumpToMessage] janela carregada', { rows: data.length });
      const windowMsgs: Message[] = (data as any[]).map(mapRpcMessageRow);
      const flags = (data as any[])[0];
      setMessages(windowMsgs);
      setIsAnchoredMode(true);
      setHasMoreMessages(Boolean(flags?.has_more_older));
      setHasMoreNewer(Boolean(flags?.has_more_newer));
      setNewMessagesWhileAnchored(0);
      return true;
    } catch (err: any) {
      // Expõe o motivo real do erro (PostgREST/PG code + message) para acelerar diagnóstico
      chatLogger.error('[jumpToMessage] erro', err);
      const reason =
        err?.message ??
        err?.error_description ??
        err?.details ??
        (typeof err === 'string' ? err : 'erro desconhecido');
      toast.error(`Não foi possível localizar a mensagem: ${reason}`);
      return false;
    } finally {
      setJumpingToMessageId(null);
    }
  };

  // Carrega N mensagens MAIS NOVAS que a última da lista (modo ancorado).
  const handleLoadMoreNewer = async () => {
    if (!selectedConversation || !hasMoreNewer || loadingMoreNewer) return;
    const last = messages[messages.length - 1];
    if (!last) return;
    try {
      setLoadingMoreNewer(true);
      const { data, error } = await supabase.rpc('get_conversation_messages_page', {
        p_conversation_id: selectedConversation.id,
        p_before_sent_at: null,
        p_page_size: MESSAGES_PAGE_SIZE,
        p_tenant_hint: currentTenant?.id ?? null,
        p_after_sent_at: last.sent_at,
      });
      if (error) throw error;
      const newer = (data || []).slice().reverse().map(mapRpcMessageRow);
      setMessages(prev => {
        const seen = new Set(prev.map(m => m.id));
        return [...prev, ...newer.filter(m => !seen.has(m.id))];
      });
      const hasMore = (data?.length || 0) === MESSAGES_PAGE_SIZE;
      setHasMoreNewer(hasMore);
      // Se alcançou o fim real, sai do modo ancorado automaticamente
      if (!hasMore) {
        setIsAnchoredMode(false);
        setNewMessagesWhileAnchored(0);
      }
    } catch (err) {
      chatLogger.error('Erro no loadMoreNewer:', err);
      toast.error('Erro ao carregar mensagens mais recentes.');
    } finally {
      setLoadingMoreNewer(false);
    }
  };

  // Sai do modo ancorado e recarrega as últimas N mensagens (modo padrão).
  const handleExitAnchorMode = () => {
    if (!selectedConversation) return;
    fetchMessages(selectedConversation.id, false, false);
  };


  // Busca contagem total de mensagens da conversa via RPC
  const fetchMessageCount = async (conversationId: string) => {
    try {
      const { data, error } = await supabase.rpc('count_conversation_messages', {
        _conversation_id: conversationId,
      });
      
      if (error) {
        chatLogger.error('Erro ao buscar contagem de mensagens:', error);
        return;
      }
      
      setTotalMessagesCount(data ?? null);
    } catch (error) {
      chatLogger.error('Erro ao buscar contagem de mensagens:', error);
    }
  };

  /**
   * Atualiza os dados da conversa selecionada (incluindo tags do contato)
   * Usado após operações que alteram dados do contato (ex: atribuir tags)
   * Mantém a seleção atual mas re-busca dados atualizados do servidor
   */
  const refreshSelectedConversation = async () => {
    if (!selectedConversation) return;
    
    try {
      chatLogger.debug('Atualizando dados da conversa selecionada', { id: selectedConversation.id });
      
      // Re-busca dados da conversa com tags do contato
      const { data, error } = await supabase
        .from('conversations')
        .select(`
          id,
          inbox_id,
          contact_id,
          group_id,
          assigned_to,
          department_id,
          status,
          last_message_at,
          last_message_preview,
          unread_count,
          created_at,
          has_non_campaign_messages,
          last_inbound_at,
          bot_paused,
          bot_paused_at,
          bot_paused_until,
          kanban_phase_id,
          contacts (
            id,
            name,
            phone,
            email,
            avatar_url,
            is_saved_to_agenda,
            metadata
          ),
          inboxes (
            id,
            name,
            channel_type,
            identifier,
            settings,
            api_providers:provider_id (
              slug
            )
          ),
          departments (
            id,
            name
          ),
          profiles:assigned_to (
            id,
            full_name,
            avatar_url
          )
        `)
        .eq('id', selectedConversation.id)
        .single();
      
      if (error) {
        chatLogger.error('Erro ao atualizar conversa selecionada:', error);
        return;
      }
      
      // Busca tags do contato se existir
      let contactTags: Array<{ id: string; name: string; color: string }> = [];
      if (data.contact_id) {
        const { data: tagAssignments } = await supabase
          .from('contact_tag_assignments')
          .select('contact_tags(id, name, color)')
          .eq('contact_id', data.contact_id);
        
        contactTags = (tagAssignments || [])
          .map(ta => ({
            id: ta.contact_tags?.id || '',
            name: ta.contact_tags?.name || '',
            color: ta.contact_tags?.color || '#6366f1',
          }))
          .filter(t => t.id);
      }
      
      // Atualiza a conversa selecionada mantendo estrutura completa
      const updatedConversation: Conversation = {
        id: data.id,
        inbox_id: data.inbox_id,
        contact_id: data.contact_id,
        contact: data.contacts ? {
          id: data.contacts.id,
          name: data.contacts.name,
          phone: data.contacts.phone,
          email: data.contacts.email,
          avatar_url: data.contacts.avatar_url,
          is_saved_to_agenda: data.contacts.is_saved_to_agenda,
          metadata: data.contacts.metadata as Record<string, unknown> | null,
          tags: contactTags,
        } : null,
        group_id: data.group_id,
        group: null,
        inbox: {
          id: data.inboxes?.id || '',
          name: data.inboxes?.name || '',
          channel_type: data.inboxes?.channel_type || '',
          identifier: data.inboxes?.identifier || '',
          settings: data.inboxes?.settings as Conversation['inbox']['settings'],
          provider_slug: (data.inboxes?.api_providers as { slug: string } | null)?.slug || null,
        },
        assigned_to: data.assigned_to,
        // Acessa dados do agente via propriedade 'profiles' retornada pelo Supabase
        assigned_agent: (data as any).profiles ? {
          id: (data as any).profiles.id,
          full_name: (data as any).profiles.full_name,
          avatar_url: (data as any).profiles.avatar_url,
        } : null,
        department_id: data.department_id,
        department: data.departments,
        status: data.status,
        last_message_at: data.last_message_at,
        last_message_preview: data.last_message_preview,
        unread_count: data.unread_count,
        created_at: data.created_at,
        has_non_campaign_messages: data.has_non_campaign_messages,
        last_inbound_at: data.last_inbound_at,
        // Human Takeover: estado de pausa do bot
        bot_paused: data.bot_paused || false,
        bot_paused_at: data.bot_paused_at || null,
        bot_paused_until: data.bot_paused_until || null,
        // kanban_phase_id é mantido para compatibilidade; o objeto kanban_phase
        // completo (com category) é resolvido pelo popover do header via
        // useContactSidebarDetails (RPC SECURITY DEFINER), já que kanban_phases
        // não expõe GRANT SELECT para authenticated e não pode ser embedado via
        // PostgREST. Ver ChatArea.tsx (headerKanbanPhase).
        kanban_phase_id: (data as any).kanban_phase_id ?? null,
      };
      
      setSelectedConversation(updatedConversation);
      chatLogger.debug('Conversa selecionada atualizada com sucesso', { 
        tags: contactTags.length 
      });
    } catch (error) {
      chatLogger.error('Erro ao atualizar conversa selecionada:', error);
    }
  };

  const markAsRead = async (conversationId: string) => {
    try {
      await supabase.rpc('mark_conversation_as_read', {
        _conversation_id: conversationId,
      });
      
      // Invalida cache para atualizar unread_count na lista
      invalidateList();
    } catch (error) {
      console.error('Error marking conversation as read:', error);
    }
  };

  // NOTA: fetchLastInboundAt removido - last_inbound_at agora vem direto da 
  // coluna conversations.last_inbound_at (atualizada automaticamente pelo trigger)
  // A atualização reativa via Realtime (linha ~600) garante feedback instantâneo

  // ===========================================================================
  // MENSAGEM PRIVADA (nota interna) — v1.0.0
  // Grava uma mensagem de sistema com metadata.kind='internal_note'.
  // Reaproveita integralmente o caminho já existente das mensagens de sistema:
  // NÃO chama send-message, portanto nunca chega ao provider nem ao visitante.
  // ===========================================================================
  const handleSendInternalNote = async (content: string, mentions: string[]) => {
    if (!selectedConversation || !user) return;
    try {
      await createSystem({
        conversationId: selectedConversation.id,
        content,
        senderId: user.id,
        metadata: {
          kind: 'internal_note',
          author_id: user.id,
          author_name: profile?.full_name || user.email?.split('@')[0] || 'Usuário',
          author_avatar: profile?.avatar_url || null,
          mentions,
        },
      });
      chatLogger.info('[handleSendInternalNote] Nota interna criada', {
        conversationId: selectedConversation.id,
        mentions: mentions.length,
      });
    } catch (error) {
      chatLogger.error('[handleSendInternalNote] Falha ao criar nota interna', error);
      toast.error('Erro ao adicionar nota interna');

      throw error;
    }
  };

  // Handler para envio de mensagem de texto (com suporte a reply, assinatura e comentários Instagram)
  const handleSendMessage = async (

    content: string, 
    replyToExternalId?: string,
    _replyToProviderMessageId?: string,
    commentOptions?: {
      reply_mode?: 'direct' | 'comment_public' | 'comment_private';
      comment_id?: string;
    },
    // @menções selecionadas no input (apenas em grupos). Cada item contém o
    // telefone do contato marcado e o nome exibido. Repassado ao send-message
    // para gerar mentions/mentioned no payload do provider (WAHA/Z-API).
    mentions?: Array<{ phone: string; name: string }>,
  ) => {
    if (!selectedConversation || !user) return;

    try {
      // Aplica assinatura do agente se habilitado nas configurações da inbox
      const contentWithSignature = applySignature(
        content,
        selectedConversation.inbox?.settings,
        profile?.full_name
      );

      // Buscar reply_to_id interno via RPC server-side (escopada por conversa)
      let replyToId: string | null = null;
      if (replyToExternalId) {
        const { data: resolvedId } = await supabase.rpc('resolve_reply_to_id_by_external', {
          p_conversation_id: selectedConversation.id,
          p_external_id: replyToExternalId,
          p_tenant_hint: currentTenant?.id ?? null,
        });
        replyToId = (resolvedId as string | null) || null;
      }

      // 1. Insert message with pending status (com assinatura aplicada)
      // Persistimos as @menções no metadata para render local consistente.
      const insertMetadata = mentions && mentions.length > 0
        ? { mentions: mentions.map(m => ({ phone: m.phone, name: m.name })) }
        : null;
      // Cria mensagem pendente via RPC segura (substitui INSERT direto em messages)
      const messageData = await createPending({
        conversationId: selectedConversation.id,
        messageType: 'text',
        content: contentWithSignature,
        senderId: user.id,
        replyToId: replyToId,
        metadata: insertMetadata,
      });

      // 2. ATUALIZAÇÃO OTIMISTA: Adiciona ao state local imediatamente como 'sent'
      // Hidrata reply_to a partir da mensagem original já presente no state local.
      // Sem isso, a bolha "Em resposta a..." só apareceria após F5 (re-fetch via RPC).
      const repliedMessage = replyToId
        ? messages.find((m) => m.id === replyToId)
        : null;
      const optimisticReplyTo = repliedMessage
        ? {
            id: repliedMessage.id,
            content: repliedMessage.content,
            message_type: repliedMessage.message_type,
            sender_type: repliedMessage.sender_type,
            attachments: (repliedMessage.attachments || []).map((att) => ({
              ...att,
              metadata: null,
            })),
          }
        : null;

      const optimisticMessage: Message = {
        id: messageData.id,
        conversation_id: messageData.conversation_id,
        sender_type: messageData.sender_type as SenderType,
        sender_id: messageData.sender_id,
        content: messageData.content,
        message_type: messageData.message_type as MessageType,
        direction: messageData.direction as MessageDirection,
        status: 'sent', // ← OTIMISTA: mostra como enviado imediatamente
        sent_at: messageData.sent_at,
        delivered_at: null,
        read_at: null,
        external_id: null,
        attachments: [],
        reply_to_id: messageData.reply_to_id,
        reply_to: optimisticReplyTo, // hidratado a partir do state local
        reactions: null,
        metadata: insertMetadata,
        deleted_at: null,
        participant_phone: null,
        // Dados do remetente (agente logado) para exibição imediata do avatar/nome
        sender: {
          id: user.id,
          full_name: profile?.full_name || null,
          avatar_url: profile?.avatar_url || null,
        },
      };
      setMessages(prev => [...prev, optimisticMessage]);

      // 3. Envia via edge function unificada em background
      const { data: sendResult, error: sendError } = await supabase.functions.invoke('send-message', {
        body: {
          inbox_id: selectedConversation.inbox_id,
          phone: selectedConversation.contact?.phone || selectedConversation.group?.external_id || '',
          content: contentWithSignature,
          message_type: 'text',
          reply_to: replyToExternalId,
          delay_typing: getRandomDelay(1, 5),
          source_type: 'chat',
          message_id: messageData.id,
          conversation_id: selectedConversation.id,
          contact_id: selectedConversation.contact?.id,
          // Parâmetros para comentários Instagram (resposta pública/privada)
          reply_mode: commentOptions?.reply_mode,
          comment_id: commentOptions?.comment_id,
          // @menções (apenas para grupos): array de telefones a marcar
          mentions: mentions?.map(m => m.phone),
        },
      });

      // 4. Atualiza status baseado no resultado
      if (sendError || !sendResult?.success) {
        console.error('Error sending message:', sendError || sendResult);

        // Extrai corpo real da resposta quando FunctionsHttpError (HTTP 4xx/5xx)
        // sendResult pode ser null quando a Edge Function retorna status não-2xx
        let errorDetails = sendResult;
        if (!errorDetails && sendError?.context) {
          try {
            errorDetails = await sendError.context.json();
          } catch {
            errorDetails = { error: sendError?.message || 'Erro desconhecido' };
          }
        }

        // Reverte para 'failed' no state local e banco, incluindo metadata de erro
        setMessages(prev => prev.map(m => m.id === messageData.id ? { ...m, status: 'failed' } : m));
        await markFailed({ messageId: messageData.id, errorPayload: createErrorMetadata(sendError, errorDetails) });
        
        // Tratamento específico para número sem WhatsApp (WAHA/Z-API)
        if (errorDetails?.error_code === 'PHONE_NOT_ON_WHATSAPP') {
          showToast.error('Este contato não tem WhatsApp', 'Verifique se o número está correto ou se o contato usa WhatsApp com outro número.');
          return;
        }
        
        // Toast com mensagem real do provider quando disponível
        const userErrorMsg = errorDetails?.error || 'Erro ao enviar mensagem';
        toast.error(userErrorMsg);
      } else {
        // Atualiza external_id no state local e banco
        setMessages(prev => prev.map(m => m.id === messageData.id ? { ...m, external_id: sendResult.external_id } : m));
        await markSent({ messageId: messageData.id, externalId: sendResult.external_id });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erro ao enviar mensagem');
    }
  };

  // Handler para envio de imagem (DEPRECATED - use handleSendImages)
  const handleSendImage = async (file: File, caption?: string) => {
    // Chama handleSendImages com array único para manter compatibilidade
    await handleSendImages([file], caption);
  };

  // Handler para envio de múltiplas imagens (cria UMA mensagem com múltiplos attachments)
  const handleSendImages = async (files: File[], caption?: string) => {
    if (!selectedConversation || !user || files.length === 0) return;

    try {
      // 1. Upload de TODAS as imagens em paralelo
      const uploadResults = await Promise.all(
        files.map(async (file, index) => {
          const timestamp = Date.now() + index;
          const extension = file.name.split('.').pop() || 'jpg';
          const storagePath = `Chats/${selectedConversation.id}/image_${timestamp}.${extension}`;
          
          const { error: uploadError } = await supabase.storage
            .from('anexos')
            .upload(storagePath, file, {
              contentType: file.type,
              upsert: false,
            });

          if (uploadError) {
            console.error('[handleSendImages] Upload error:', uploadError);
            throw uploadError;
          }

          const { data: publicUrlData } = supabase.storage
            .from('anexos')
            .getPublicUrl(storagePath);

          return { 
            storagePath, 
            publicUrl: publicUrlData.publicUrl, 
            file,
            mimeType: file.type,
            sizeBytes: file.size,
          };
        })
      );

      // 2. Gera preview para a conversa
      const contentText = files.length > 1 
        ? `📷 ${files.length} imagens${caption ? ` - ${caption}` : ''}`
        : caption || '📷 Imagem';

      // 3. Cria UMA única mensagem com status 'pending'
      // Cria mensagem pendente via RPC segura (substitui INSERT direto em messages)
      const messageData = await createPending({
        conversationId: selectedConversation.id,
        messageType: 'image',
        content: contentText,
        senderId: user.id,
      });

      // 4. Cria MÚLTIPLOS attachments vinculados a essa mensagem
      const attachmentsToInsert = uploadResults.map(({ storagePath, publicUrl, mimeType, sizeBytes }) => ({
        message_id: messageData.id,
        type: 'image' as const,
        url: storagePath,
        mime_type: mimeType,
        size_bytes: sizeBytes,
      }));

      // Anexa via RPC segura em lote (substitui INSERT direto em message_attachments)
      const insertedAttachments: Array<{ id?: string }> | null = null;
      try {
        await attachMedia(messageData.id, attachmentsToInsert as never);
      } catch (e) {
        console.error('[attachMedia] erro:', e);
      }

      // 5. ATUALIZAÇÃO OTIMISTA: Adiciona ao state local imediatamente como 'sent'
      const optimisticAttachments: MessageAttachment[] = uploadResults.map(({ storagePath, publicUrl, mimeType, sizeBytes }, index) => ({
        id: insertedAttachments?.[index]?.id || `temp-${index}`,
        type: 'image',
        url: publicUrl,
        mime_type: mimeType,
        filename: null,
        size_bytes: sizeBytes,
        metadata: null,
      }));

      const optimisticMessage: Message = {
        id: messageData.id,
        conversation_id: messageData.conversation_id,
        sender_type: messageData.sender_type as SenderType,
        sender_id: messageData.sender_id,
        content: messageData.content,
        message_type: messageData.message_type as MessageType,
        direction: messageData.direction as MessageDirection,
        status: 'sent', // ← OTIMISTA
        sent_at: messageData.sent_at,
        delivered_at: null,
        read_at: null,
        external_id: null,
        attachments: optimisticAttachments,
        reply_to_id: null,
        reply_to: null,
        reactions: null,
        metadata: null,
        deleted_at: null,
        participant_phone: null,
        // Dados do remetente (agente logado) para exibição imediata do avatar/nome
        sender: {
          id: user.id,
          full_name: profile?.full_name || null,
          avatar_url: profile?.avatar_url || null,
        },
      };
      setMessages(prev => [...prev, optimisticMessage]);

      // 6. Envia cada imagem via provider em background
      // Variáveis de controle do loop de envio
      let allSuccess = true;
      let firstExternalId: string | null = null;
      let firstSendError: any = null; // Armazena primeiro erro real do provider para metadata detalhada

      for (let i = 0; i < uploadResults.length; i++) {
        const { publicUrl } = uploadResults[i];
        const imageCaption = i === 0 ? caption : undefined;
        
        const { data: sendResult, error: sendError } = await supabase.functions.invoke('send-message', {
          body: {
            inbox_id: selectedConversation.inbox_id,
            phone: selectedConversation.contact?.phone || selectedConversation.group?.external_id || '',
            message_type: 'image',
            media_url: publicUrl,
            media_caption: imageCaption,
            source_type: 'chat',
            message_id: messageData.id,
            conversation_id: selectedConversation.id,
            contact_id: selectedConversation.contact?.id,
          },
        });

        if (sendError || !sendResult?.success) {
          console.error('[handleSendImages] Send error:', sendError || sendResult);
          allSuccess = false;
          // Extrai corpo real da resposta quando FunctionsHttpError (HTTP 4xx/5xx)
          let imgErrorDetails = sendResult;
          if (!imgErrorDetails && sendError?.context) {
            try {
              imgErrorDetails = await sendError.context.json();
            } catch {
              imgErrorDetails = { error: sendError?.message || 'Erro desconhecido' };
            }
          }
          // Captura o primeiro erro real para propagar na metadata
          if (!firstSendError) {
            firstSendError = imgErrorDetails || { error: sendError?.message || 'Erro desconhecido' };
          }
        } else if (i === 0) {
          firstExternalId = sendResult.external_id;
        }
      }

      // 7. Atualiza status final
      if (allSuccess) {
        setMessages(prev => prev.map(m => m.id === messageData.id ? { ...m, external_id: firstExternalId } : m));
        await markSent({ messageId: messageData.id, externalId: firstExternalId });
        if (files.length > 1) {
          toast.success(`${files.length} imagens enviadas com sucesso`);
        }
      } else {
        setMessages(prev => prev.map(m => m.id === messageData.id ? { ...m, status: 'failed' } : m));
        // Propaga erro real do provider (ex: janela 24h Instagram) ao invés de genérico
        // Marca como failed via RPC; preserva external_id da primeira imagem no metadata
        await markFailed({
          messageId: messageData.id,
          errorPayload: {
            ...(createErrorMetadata(
              new Error(firstSendError?.error || 'Erro ao enviar algumas imagens'),
              firstSendError,
            ) as Record<string, unknown>),
            external_id: firstExternalId,
          },
        });
        // Exibe mensagem do provider quando disponível
        const errorMsg = firstSendError?.error || 'Erro ao enviar algumas imagens';
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('[handleSendImages] Error:', error);
      toast.error('Erro ao enviar imagens');
    }
  };

  // Handler para envio de vídeo
  const handleSendVideo = async (file: File, caption?: string) => {
    if (!selectedConversation || !user) return;

    try {
      // 1. Upload do vídeo para Supabase Storage
      const timestamp = Date.now();
      const extension = file.name.split('.').pop() || 'mp4';
      const storagePath = `Chats/${selectedConversation.id}/video_${timestamp}.${extension}`;
      
      const { error: uploadError } = await supabase.storage
        .from('anexos')
        .upload(storagePath, file, {
          contentType: file.type,
          upsert: false,
        });

      if (uploadError) throw uploadError;

      const { data: publicUrlData } = supabase.storage
        .from('anexos')
        .getPublicUrl(storagePath);

      // 2. Cria mensagem com status 'pending'
      // Cria mensagem pendente via RPC segura (substitui INSERT direto em messages)
      const messageData = await createPending({
        conversationId: selectedConversation.id,
        messageType: 'video',
        content: caption || '🎥 Vídeo',
        senderId: user.id,
      });

      // 3. Cria attachment
      // Anexa via RPC segura (substitui INSERT direto em message_attachments)
      try { const att = {
        message_id: messageData.id,
        type: 'video',
        url: storagePath,
        mime_type: file.type,
        filename: file.name,
        size_bytes: file.size,
      }; await attachMedia(att.message_id, [att]); }
      catch (e) { console.error('[attachMedia] erro:', e); }

      // 4. Atualização otimista
      const optimisticMessage: Message = {
        id: messageData.id,
        conversation_id: messageData.conversation_id,
        sender_type: messageData.sender_type as SenderType,
        sender_id: messageData.sender_id,
        content: messageData.content,
        message_type: messageData.message_type as MessageType,
        direction: messageData.direction as MessageDirection,
        status: 'sent',
        sent_at: messageData.sent_at,
        delivered_at: null,
        read_at: null,
        external_id: null,
        attachments: [{
          id: 'temp-0',
          type: 'video',
          url: publicUrlData.publicUrl,
          mime_type: file.type,
          filename: file.name,
          size_bytes: file.size,
          metadata: null,
        }],
        reply_to_id: null,
        reply_to: null,
        reactions: null,
        metadata: null,
        deleted_at: null,
        participant_phone: null,
        sender: {
          id: user.id,
          full_name: profile?.full_name || null,
          avatar_url: profile?.avatar_url || null,
        },
      };
      setMessages(prev => [...prev, optimisticMessage]);

      // 5. Envia via provider
      const { data: sendResult, error: sendError } = await supabase.functions.invoke('send-message', {
        body: {
          inbox_id: selectedConversation.inbox_id,
          phone: selectedConversation.contact?.phone || selectedConversation.group?.external_id || '',
          message_type: 'video',
          media_url: publicUrlData.publicUrl,
          media_caption: caption,
          media_mime_type: file.type || 'video/mp4',
          media_filename: file.name,
          source_type: 'chat',
          message_id: messageData.id,
          conversation_id: selectedConversation.id,
          contact_id: selectedConversation.contact?.id,
        },
      });

      // 6. Atualiza status final
      const finalStatus = (sendError || !sendResult?.success) ? 'failed' : 'sent';
      // Atualiza status final via RPC segura (substitui UPDATE direto em messages)
      if (finalStatus === 'sent') {
        await markSent({ messageId: messageData.id, externalId: sendResult?.external_id ?? null });
      } else {
        await markFailed({ messageId: messageData.id });
      }

      setMessages(prev => prev.map(m => 
        m.id === messageData.id ? { ...m, status: finalStatus, external_id: sendResult?.external_id } : m
      ));

      if (finalStatus === 'failed') {
        toast.error('Erro ao enviar vídeo');
      }
    } catch (error) {
      console.error('[handleSendVideo] Error:', error);
      toast.error('Erro ao enviar vídeo');
    }
  };

  // Handler para envio de documento
  const handleSendDocument = async (file: File, customFileName?: string) => {
    if (!selectedConversation || !user) return;

    try {
      // 1. Upload para Supabase Storage
      const timestamp = Date.now();
      const extension = file.name.split('.').pop() || 'bin';
      const storagePath = `Chats/${selectedConversation.id}/doc_${timestamp}.${extension}`;
      
      const { error: uploadError } = await supabase.storage
        .from('anexos')
        .upload(storagePath, file, {
          contentType: file.type,
          upsert: false,
        });

      if (uploadError) {
        console.error('[handleSendDocument] Upload error:', uploadError);
        throw uploadError;
      }

      // 2. Obtém URL pública do documento
      const { data: publicUrlData } = supabase.storage
        .from('anexos')
        .getPublicUrl(storagePath);

      const documentUrl = publicUrlData.publicUrl;

      // 3. Define o nome do arquivo para exibição
      const fileName = customFileName || file.name;

      // 4. Insert message with pending status
      // Cria mensagem pendente via RPC segura (substitui INSERT direto em messages)
      const messageData = await createPending({
        conversationId: selectedConversation.id,
        messageType: 'document',
        content: `📄 ${fileName}`,
        senderId: user.id,
      });

      // 5. Insert attachment record
      // Anexa via RPC segura (substitui INSERT direto em message_attachments)
      const attachmentData: { id?: string } | null = null;
      const attachmentError: unknown = null;
      try {
        const att = {
          message_id: messageData.id,
          type: 'document',
          url: storagePath,
          mime_type: file.type,
          filename: fileName,
          size_bytes: file.size,
        };
        await attachMedia(att.message_id, [att]);
      } catch (e) {
        console.error('[attachMedia] erro:', e);
      }

      // 6. ATUALIZAÇÃO OTIMISTA: Adiciona ao state local imediatamente como 'sent'
      const optimisticMessage: Message = {
        id: messageData.id,
        conversation_id: messageData.conversation_id,
        sender_type: messageData.sender_type as SenderType,
        sender_id: messageData.sender_id,
        content: messageData.content,
        message_type: messageData.message_type as MessageType,
        direction: messageData.direction as MessageDirection,
        status: 'sent', // ← OTIMISTA
        sent_at: messageData.sent_at,
        delivered_at: null,
        read_at: null,
        external_id: null,
        attachments: [{
          id: attachmentData?.id || 'temp',
          type: 'document',
          url: documentUrl,
          mime_type: file.type,
          filename: fileName,
          size_bytes: file.size,
          metadata: null,
        }],
        reply_to_id: null,
        reply_to: null,
        reactions: null,
        metadata: null,
        deleted_at: null,
        participant_phone: null,
        // Dados do remetente (agente logado) para exibição imediata do avatar/nome
        sender: {
          id: user.id,
          full_name: profile?.full_name || null,
          avatar_url: profile?.avatar_url || null,
        },
      };
      setMessages(prev => [...prev, optimisticMessage]);

      // 7. Envia via edge function em background
      const { data: sendResult, error: sendError } = await supabase.functions.invoke('send-message', {
        body: {
          inbox_id: selectedConversation.inbox_id,
          phone: selectedConversation.contact?.phone || selectedConversation.group?.external_id || '',
          message_type: 'document',
          media_url: documentUrl,
          media_caption: fileName,
          media_mime_type: file.type || 'application/pdf',  // MIME type explícito para evitar fallback para application/octet-stream
          media_filename: fileName,                          // Nome do arquivo explícito para providers
          source_type: 'chat',
          message_id: messageData.id,
          conversation_id: selectedConversation.id,
          contact_id: selectedConversation.contact?.id,
        },
      });

      // 8. Atualiza status baseado no resultado
      if (sendError || !sendResult?.success) {
        console.error('[handleSendDocument] Z-API error:', sendError || sendResult);
        setMessages(prev => prev.map(m => m.id === messageData.id ? { ...m, status: 'failed' } : m));
        await markFailed({ messageId: messageData.id, errorPayload: createErrorMetadata(sendError, sendResult) });
        toast.error('Erro ao enviar documento via WhatsApp');
      } else {
        setMessages(prev => prev.map(m => m.id === messageData.id ? { ...m, external_id: sendResult.external_id } : m));
        await markSent({ messageId: messageData.id, externalId: sendResult.external_id });
        chatLogger.debug('[handleSendDocument] Documento enviado com sucesso:', sendResult.external_id);
      }
    } catch (error) {
      console.error('[handleSendDocument] Error:', error);
      toast.error('Erro ao enviar documento');
    }
  };

  // Handler para envio de áudio
  // Áudio vem em OGG/Opus (opus-media-recorder). Para inboxes Meta WABA o blob
  // é canonicalizado no cliente (ver src/lib/audio/convertToMp3.ts) e enviado
  // SEMPRE como nota de voz OGG/Opus — sem fallback para MP3.
  // contornando o erro 131053 da Cloud API sem custo de servidor.
  const handleSendAudio = async (audioBlob: Blob, duration: number) => {
    if (!selectedConversation || !user) return;

    try {
      // 0. Prepara o áudio conforme o provider da inbox (OGG nativo ou MP3 para Meta)
      const prepared = await prepareAudioForProvider(
        audioBlob,
        selectedConversation.inbox?.provider_slug,
      );

      // 1. Upload para Supabase Storage (extensão/MIME coerentes com o blob real)
      const timestamp = Date.now();
      const storagePath = `Chats/${selectedConversation.id}/audio_${timestamp}.${prepared.extension}`;
      
      const { error: uploadError } = await supabase.storage
        .from('anexos')
        .upload(storagePath, prepared.blob, {
          contentType: prepared.mimeType,
          upsert: false,
        });

      if (uploadError) {
        console.error('[handleSendAudio] Upload error:', uploadError);
        throw uploadError;
      }

      // Obtém URL pública do áudio
      const { data: publicUrlData } = supabase.storage
        .from('anexos')
        .getPublicUrl(storagePath);

      const audioUrl = publicUrlData.publicUrl;

      // 2. Insert message with pending status
      // Cria mensagem pendente via RPC segura (substitui INSERT direto em messages)
      const messageData = await createPending({
        conversationId: selectedConversation.id,
        messageType: 'audio',
        content: '🎤 Áudio',
        senderId: user.id,
      });

      // 3. Insert attachment record
      // Anexa via RPC segura (substitui INSERT direto em message_attachments)
      const attachmentData: { id?: string } | null = null;
      const attachmentError: unknown = null;
      try {
        const att = {
          message_id: messageData.id,
          type: 'audio',
          url: storagePath,
          mime_type: prepared.mimeType,
          size_bytes: prepared.blob.size,
          metadata: {
            seconds: duration,
            ptt: prepared.isVoiceNote,
          },
        };
        await attachMedia(att.message_id, [att]);
      } catch (e) {
        console.error('[attachMedia] erro:', e);
      }


      // 4. ATUALIZAÇÃO OTIMISTA: Adiciona ao state local imediatamente como 'pending'
      // O áudio aparece no chat com relógio enquanto o envio ocorre em background
      const optimisticMessage: Message = {
        id: messageData.id,
        conversation_id: messageData.conversation_id,
        sender_type: messageData.sender_type as SenderType,
        sender_id: messageData.sender_id,
        content: messageData.content,
        message_type: messageData.message_type as MessageType,
        direction: messageData.direction as MessageDirection,
        status: 'pending', // ← Status real enquanto aguarda envio em background
        sent_at: messageData.sent_at,
        delivered_at: null,
        read_at: null,
        external_id: null,
        attachments: [{
          id: attachmentData?.id || 'temp',
          type: 'audio',
          url: audioUrl,
          mime_type: prepared.mimeType,
          filename: null,
          size_bytes: prepared.blob.size,
          metadata: { seconds: duration, ptt: prepared.isVoiceNote },
        }],
        reply_to_id: null,
        reply_to: null,
        reactions: null,
        metadata: null,
        deleted_at: null,
        participant_phone: null,
        // Dados do remetente (agente logado) para exibição imediata do avatar/nome
        sender: {
          id: user.id,
          full_name: profile?.full_name || null,
          avatar_url: profile?.avatar_url || null,
        },
      };
      setMessages(prev => [...prev, optimisticMessage]);

      // ========== PONTO DE CORTE: UI LIBERADA AQUI ==========
      // A partir daqui, o envio via Edge Function ocorre em BACKGROUND
      // Isso permite que o usuário grave múltiplos áudios em sequência sem travamento
      
      // 5. Envia via Edge Function em BACKGROUND (fire-and-forget)
      // Captura messageId para uso no callback
      const messageId = messageData.id;
      
      supabase.functions.invoke('send-message', {
        body: {
          inbox_id: selectedConversation.inbox_id,
          phone: selectedConversation.contact?.phone || selectedConversation.group?.external_id || '',
          message_type: 'audio',
          media_url: audioUrl,
          waveform: prepared.isVoiceNote,
          // MIME real do arquivo enviado (nota de voz OGG/Opus ou MP3 básico)
          media_mime_type: prepared.mode === 'basic_mp3' ? 'audio/mpeg' : 'audio/ogg; codecs=opus',

          delay_typing: getRandomDelay(1, 8),
          source_type: 'chat',
          message_id: messageId,
          conversation_id: selectedConversation.id,
          contact_id: selectedConversation.contact?.id,
        },
      }).then(async ({ data: sendResult, error: sendError }) => {
        // 6. Atualiza status baseado no resultado (em background)
        if (sendError || !sendResult?.success) {
          console.error('[handleSendAudio] Erro no envio background:', sendError || sendResult);

          // Extrai corpo real da resposta quando FunctionsHttpError (HTTP 4xx/5xx)
          let audioErrorDetails = sendResult;
          if (!audioErrorDetails && sendError?.context) {
            try {
              audioErrorDetails = await sendError.context.json();
            } catch {
              audioErrorDetails = { error: sendError?.message || 'Erro desconhecido' };
            }
          }

          setMessages(prev => prev.map(m => m.id === messageId ? { ...m, status: 'failed' } : m));
          await markFailed({ messageId: messageId, errorPayload: createErrorMetadata(sendError, audioErrorDetails) });
          // Toast com mensagem real do provider quando disponível
          const audioErrorMsg = audioErrorDetails?.error || 'Erro ao enviar áudio';
          toast.error(audioErrorMsg);
        } else {
          setMessages(prev => prev.map(m => m.id === messageId ? { ...m, status: 'sent', external_id: sendResult.external_id } : m));
          await markSent({ messageId: messageId, externalId: sendResult.external_id });
          chatLogger.debug('[handleSendAudio] Áudio enviado com sucesso:', sendResult.external_id);
        }
      }).catch(async (error) => {
        console.error('[handleSendAudio] Erro crítico no envio background:', error);
        setMessages(prev => prev.map(m => m.id === messageId ? { ...m, status: 'failed' } : m));
        // Grava metadata de erro no banco para exibição no modal de detalhes
        await markFailed({ messageId: messageId, errorPayload: createErrorMetadata(error) });
      });
      
      // Retorna imediatamente - não aguarda envio completar
      // O áudio aparece no chat como "pending" e atualiza para "sent" quando o background completa
      
    } catch (error) {
      console.error('[handleSendAudio] Erro inicial:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao enviar áudio');
    }
  };

  // Handler para envio de localização
  const handleSendLocation = async (location: {
    latitude: number;
    longitude: number;
    name?: string;
    address?: string;
  }) => {
    if (!selectedConversation || !user) return;

    try {
      // 1. Insert message with pending status
      // Cria mensagem pendente via RPC segura (substitui INSERT direto em messages)
      const messageData = await createPending({
        conversationId: selectedConversation.id,
        messageType: 'location',
        content: location.name || location.address || '📍 Localização',
        senderId: user.id,
      });

      // 2. Insert attachment with location metadata
      // Anexa via RPC segura (substitui INSERT direto em message_attachments)
      const attachmentData: { id?: string } | null = null;
      const attachmentError: unknown = null;
      try {
        const att = {
          message_id: messageData.id,
          type: 'location',
          url: `https://maps.google.com/?q=${location.latitude},${location.longitude}`,
          metadata: {
            latitude: location.latitude,
            longitude: location.longitude,
            name: location.name,
            address: location.address,
          },
        };
        await attachMedia(att.message_id, [att]);
      } catch (e) {
        console.error('[attachMedia] erro:', e);
      }

      // 3. ATUALIZAÇÃO OTIMISTA: Adiciona ao state local imediatamente como 'sent'
      const optimisticMessage: Message = {
        id: messageData.id,
        conversation_id: messageData.conversation_id,
        sender_type: messageData.sender_type as SenderType,
        sender_id: messageData.sender_id,
        content: messageData.content,
        message_type: messageData.message_type as MessageType,
        direction: messageData.direction as MessageDirection,
        status: 'sent', // ← OTIMISTA
        sent_at: messageData.sent_at,
        delivered_at: null,
        read_at: null,
        external_id: null,
        attachments: [{
          id: attachmentData?.id || 'temp',
          type: 'location',
          url: `https://maps.google.com/?q=${location.latitude},${location.longitude}`,
          mime_type: null,
          filename: null,
          size_bytes: null,
          metadata: { latitude: location.latitude, longitude: location.longitude, name: location.name, address: location.address },
        }],
        reply_to_id: null,
        reply_to: null,
        reactions: null,
        metadata: null,
        deleted_at: null,
        participant_phone: null,
        // Dados do remetente (agente logado) para exibição imediata do avatar/nome
        sender: {
          id: user.id,
          full_name: profile?.full_name || null,
          avatar_url: profile?.avatar_url || null,
        },
      };
      setMessages(prev => [...prev, optimisticMessage]);

      // 4. Envia via edge function em background
      const { data: sendResult, error: sendError } = await supabase.functions.invoke('send-message', {
        body: {
          inbox_id: selectedConversation.inbox_id,
          phone: selectedConversation.contact?.phone || selectedConversation.group?.external_id || '',
          message_type: 'location',
          latitude: location.latitude,
          longitude: location.longitude,
          location_name: location.name,
          location_address: location.address,
          source_type: 'chat',
          message_id: messageData.id,
          conversation_id: selectedConversation.id,
          contact_id: selectedConversation.contact?.id,
        },
      });

      // 5. Atualiza status baseado no resultado
      if (sendError || !sendResult?.success) {
        console.error('[handleSendLocation] Z-API error:', sendError || sendResult);
        setMessages(prev => prev.map(m => m.id === messageData.id ? { ...m, status: 'failed' } : m));
        await markFailed({ messageId: messageData.id, errorPayload: createErrorMetadata(sendError, sendResult) });
        toast.error('Erro ao enviar localização via WhatsApp');
      } else {
        setMessages(prev => prev.map(m => m.id === messageData.id ? { ...m, external_id: sendResult.external_id } : m));
        await markSent({ messageId: messageData.id, externalId: sendResult.external_id });
        chatLogger.debug('[handleSendLocation] Localização enviada com sucesso:', sendResult.external_id);
      }
    } catch (error) {
      console.error('[handleSendLocation] Error:', error);
      toast.error('Erro ao enviar localização');
    }
  };

  // Handler para envio de contato
  const handleSendContact = async (contact: {
    name: string;
    phone: string;
    businessDescription?: string;
  }) => {
    if (!selectedConversation || !user) return;

    try {
      // 1. Insert message with pending status
      // Cria mensagem pendente via RPC segura (substitui INSERT direto em messages)
      const messageData = await createPending({
        conversationId: selectedConversation.id,
        messageType: 'contact', // Cast necessário pois contact não está no enum,
        content: `👤 ${contact.name}`,
        senderId: user.id,
      });

      // 2. Insert attachment with contact metadata
      // Anexa via RPC segura (substitui INSERT direto em message_attachments)
      const attachmentData: { id?: string } | null = null;
      const attachmentError: unknown = null;
      try {
        const att = {
          message_id: messageData.id,
          type: 'contact',
          url: `tel:${contact.phone.replace(/\D/g, '')}`,
          metadata: {
            displayName: contact.name,
            phone: contact.phone,
            businessDescription: contact.businessDescription,
          },
        };
        await attachMedia(att.message_id, [att]);
      } catch (e) {
        console.error('[attachMedia] erro:', e);
      }

      // 3. ATUALIZAÇÃO OTIMISTA: Adiciona ao state local imediatamente como 'sent'
      const optimisticMessage: Message = {
        id: messageData.id,
        conversation_id: messageData.conversation_id,
        sender_type: messageData.sender_type as SenderType,
        sender_id: messageData.sender_id,
        content: messageData.content,
        message_type: messageData.message_type as MessageType,
        direction: messageData.direction as MessageDirection,
        status: 'sent', // ← OTIMISTA
        sent_at: messageData.sent_at,
        delivered_at: null,
        read_at: null,
        external_id: null,
        attachments: [{
          id: attachmentData?.id || 'temp',
          type: 'contact',
          url: `tel:${contact.phone.replace(/\D/g, '')}`,
          mime_type: null,
          filename: null,
          size_bytes: null,
          metadata: { displayName: contact.name, phone: contact.phone, businessDescription: contact.businessDescription },
        }],
        reply_to_id: null,
        reply_to: null,
        reactions: null,
        metadata: null,
        deleted_at: null,
        participant_phone: null,
        // Dados do remetente (agente logado) para exibição imediata do avatar/nome
        sender: {
          id: user.id,
          full_name: profile?.full_name || null,
          avatar_url: profile?.avatar_url || null,
        },
      };
      setMessages(prev => [...prev, optimisticMessage]);

      // 4. Envia via edge function em background
      const { data: sendResult, error: sendError } = await supabase.functions.invoke('send-message', {
        body: {
          inbox_id: selectedConversation.inbox_id,
          phone: selectedConversation.contact?.phone || selectedConversation.group?.external_id || '',
          message_type: 'contact',
          contact_name: contact.name,
          contact_phone: contact.phone,
          contact_business_description: contact.businessDescription,
          source_type: 'chat',
          message_id: messageData.id,
          conversation_id: selectedConversation.id,
          contact_id: selectedConversation.contact?.id,
        },
      });

      // 5. Atualiza status baseado no resultado
      if (sendError || !sendResult?.success) {
        console.error('[handleSendContact] Z-API error:', sendError || sendResult);
        setMessages(prev => prev.map(m => m.id === messageData.id ? { ...m, status: 'failed' } : m));
        await markFailed({ messageId: messageData.id, errorPayload: createErrorMetadata(sendError, sendResult) });
        toast.error('Erro ao enviar contato via WhatsApp');
      } else {
        setMessages(prev => prev.map(m => m.id === messageData.id ? { ...m, external_id: sendResult.external_id } : m));
        await markSent({ messageId: messageData.id, externalId: sendResult.external_id });
        chatLogger.debug('[handleSendContact] Contato enviado com sucesso:', sendResult.external_id);
      }
    } catch (error) {
      console.error('[handleSendContact] Error:', error);
      toast.error('Erro ao enviar contato');
    }
  };

  // Handler para envio de botão PIX (chave PIX copiável)
  const handleSendPix = async (pix: {
    name: string;
    key: string;
    keyType: 'CPF' | 'CNPJ' | 'EMAIL' | 'CELULAR' | 'ALEATORIA';
    message?: string;
  }) => {
    if (!selectedConversation || !user) return;

    try {
      // 1. Insert message with pending status
      // Cria mensagem pendente via RPC segura (substitui INSERT direto em messages)
      const messageData = await createPending({
        conversationId: selectedConversation.id,
        messageType: 'button_pix',
        content: pix.message || `💰 PIX: ${pix.name}`,
        senderId: user.id,
      });

      // 2. Insert attachment with PIX metadata
      // Anexa via RPC segura (substitui INSERT direto em message_attachments)
      const attachmentData: { id?: string } | null = null;
      const attachmentError: unknown = null;
      try {
        const att = {
          message_id: messageData.id,
          type: 'pix',
          url: `pix:${pix.key}`,
          metadata: {
            name: pix.name,
            pixKey: pix.key,
            keyType: pix.keyType,
            message: pix.message,
          },
        };
        await attachMedia(att.message_id, [att]);
      } catch (e) {
        console.error('[attachMedia] erro:', e);
      }

      // 3. ATUALIZAÇÃO OTIMISTA: Adiciona ao state local imediatamente como 'sent'
      const optimisticMessage: Message = {
        id: messageData.id,
        conversation_id: messageData.conversation_id,
        sender_type: messageData.sender_type as SenderType,
        sender_id: messageData.sender_id,
        content: messageData.content,
        message_type: messageData.message_type as MessageType,
        direction: messageData.direction as MessageDirection,
        status: 'sent', // ← OTIMISTA
        sent_at: messageData.sent_at,
        delivered_at: null,
        read_at: null,
        external_id: null,
        attachments: [{
          id: attachmentData?.id || 'temp',
          type: 'pix',
          url: `pix:${pix.key}`,
          mime_type: null,
          filename: null,
          size_bytes: null,
          metadata: { name: pix.name, pixKey: pix.key, keyType: pix.keyType, message: pix.message },
        }],
        reply_to_id: null,
        reply_to: null,
        reactions: null,
        metadata: null,
        deleted_at: null,
        participant_phone: null,
        // Dados do remetente (agente logado) para exibição imediata do avatar/nome
        sender: {
          id: user.id,
          full_name: profile?.full_name || null,
          avatar_url: profile?.avatar_url || null,
        },
      };
      setMessages(prev => [...prev, optimisticMessage]);

      // 4. Envia via edge function em background
      const { data: sendResult, error: sendError } = await supabase.functions.invoke('send-message', {
        body: {
          inbox_id: selectedConversation.inbox_id,
          phone: selectedConversation.contact?.phone || selectedConversation.group?.external_id || '',
          message_type: 'button_pix',
          content: pix.message || '',
          pix_key: pix.key,
          pix_key_type: pix.keyType,
          pix_name: pix.name,
          source_type: 'chat',
          message_id: messageData.id,
          conversation_id: selectedConversation.id,
          contact_id: selectedConversation.contact?.id,
        },
      });

      // 5. Atualiza status baseado no resultado
      if (sendError || !sendResult?.success) {
        console.error('[handleSendPix] Z-API error:', sendError || sendResult);
        setMessages(prev => prev.map(m => m.id === messageData.id ? { ...m, status: 'failed' } : m));
        await markFailed({ messageId: messageData.id, errorPayload: createErrorMetadata(sendError, sendResult) });
        toast.error('Erro ao enviar PIX via WhatsApp');
      } else {
        setMessages(prev => prev.map(m => m.id === messageData.id ? { ...m, external_id: sendResult.external_id } : m));
        await markSent({ messageId: messageData.id, externalId: sendResult.external_id });
        chatLogger.debug('[handleSendPix] PIX enviado com sucesso:', sendResult.external_id);
      }
    } catch (error) {
      console.error('[handleSendPix] Error:', error);
      toast.error('Erro ao enviar PIX');
    }
  };

  /**
   * Handler para envio de reação a uma mensagem
   * Chama a edge function send-message com tipo 'reaction'
   * Para remover reação, passe emoji vazio ''
   * 
   * @param message - Objeto Message completo (inclui metadata.z_api_phone)
   * @param emoji - Emoji da reação ou '' para remover
   */
  const handleSendReaction = async (message: Message, emoji: string) => {
    if (!selectedConversation || !message.external_id) return;

    // =========================================================================
    // PASSO 1: Calcular reações ANTES da chamada API (padrão otimista)
    // =========================================================================
    const metadata = message.metadata as { z_api_phone?: string; providerMessageId?: string } | null;
    const phone = metadata?.z_api_phone || selectedConversation.contact?.phone || selectedConversation.group?.external_id || '';

    // Identificador de quem reagiu (telefone da inbox - apenas dígitos)
    const rawIdentifier = selectedConversation.inbox.identifier || '';
    const reactedBy = rawIdentifier.replace(/\D/g, '') || 'agent';

    // Helper para normalizar identificadores na comparação
    const normalizeId = (id: string) => id.replace(/\D/g, '') || id;

    // Salvar estado anterior para rollback
    const previousReactions = message.reactions || [];

    // Processar reações: remover reação anterior do mesmo agente
    const filteredReactions = previousReactions.filter(
      r => normalizeId(r.reacted_by) !== normalizeId(reactedBy)
    );

    // Se tem emoji, adiciona; se vazio, apenas remove
    const updatedReactions: MessageReaction[] = emoji
      ? [...filteredReactions, {
          emoji,
          reacted_by: reactedBy,
          reacted_at: Date.now()
        }]
      : filteredReactions;

    // =========================================================================
    // PASSO 2: Atualizar UI IMEDIATAMENTE (~0ms) - padrão otimista
    // =========================================================================
    setMessages(prev => prev.map(m =>
      m.id === message.id
        ? { ...m, reactions: updatedReactions }
        : m
    ));

    // =========================================================================
    // PASSO 3: Background - enviar para API e banco, rollback se falhar
    // =========================================================================
    try {
      // Para Notificame (WhatsApp Oficial), usar providerMessageId do metadata (Base64)
      const isNotificame = selectedConversation.inbox?.channel_type === 'whatsapp_oficial';
      const reactionMessageId = (isNotificame && metadata?.providerMessageId)
        ? metadata.providerMessageId
        : message.external_id;

      chatLogger.debug('Enviando reação (otimista)', {
        isNotificame,
        reactionMessageId: reactionMessageId?.substring(0, 30),
        emoji
      });

      // Passa contact_id para Instagram que não usa phone (usa channel_ids.instagram_igsid)
      const result = await sendReaction(
        selectedConversation.inbox_id,
        phone,
        reactionMessageId,
        emoji,
        message.direction,
        selectedConversation.contact?.id
      );

      if (result.success) {
        // API OK - persistir reação no banco via RPC segura (Fase 4)
        try {
          await setReaction(message.id, updatedReactions);
        } catch (updateError) {
          console.error('[handleSendReaction] Erro ao salvar reação no banco:', updateError);
        }
      } else {
        // API falhou - ROLLBACK da UI
        setMessages(prev => prev.map(m =>
          m.id === message.id
            ? { ...m, reactions: previousReactions }
            : m
        ));
        toast.error('Erro ao enviar reação');
      }
    } catch (error) {
      // Exceção - ROLLBACK da UI
      console.error('[handleSendReaction] Error:', error);
      setMessages(prev => prev.map(m =>
        m.id === message.id
          ? { ...m, reactions: previousReactions }
          : m
      ));
      toast.error('Erro ao enviar reação');
    }
  };

  /**
   * Handler para deletar uma mensagem
   * 
   * Se a mensagem foi enviada há menos de ~1h, tenta deletar no WhatsApp também.
   * Se deleteForEveryone=true, tenta deletar no WhatsApp também.
   * Se deleteForEveryone=false, deleta apenas localmente.
   * 
   * @param message - Mensagem a ser deletada
   * @param deleteForEveryone - Se true, tenta deletar no WhatsApp; se false, deleta só local
   */
  const handleDeleteMessage = async (message: Message, deleteForEveryone: boolean) => {
    if (!selectedConversation) return;

    try {
      let deletedOnWhatsApp = false;

      // Se o usuário escolheu "Apagar para todos", tenta deletar no WhatsApp
      if (deleteForEveryone && message.external_id && message.direction === 'outbound') {
        const metadata = message.metadata as { z_api_phone?: string } | null;
        const phone = metadata?.z_api_phone || selectedConversation.contact?.phone || selectedConversation.group?.external_id || '';
        
        chatLogger.debug('[handleDeleteMessage] Tentando deletar no WhatsApp:', message.external_id);
        
        const result = await deleteMessage(
          selectedConversation.inbox_id,
          phone,
          message.external_id,
          true // owner=true (mensagem enviada por mim)
        );
        
        deletedOnWhatsApp = result.deletedOnWhatsApp;
        
        if (!result.success) {
          chatLogger.warn('[handleDeleteMessage] Falha ao deletar no WhatsApp:', result.error);
        }
      }

      // Soft delete via RPC segura (Fase 4): valida tenant e grava deleted_at.
      let deletedAt: string;
      try {
        deletedAt = await softDelete(message.id);
      } catch (deleteError) {
        console.error('[handleDeleteMessage] Erro ao deletar do banco:', deleteError);
        toast.error('Erro ao deletar mensagem');
        return;
      }

      // Atualizar estado local para refletir soft delete (não remove, apenas marca)
      setMessages(prev => prev.map(m =>
        m.id === message.id
          ? { ...m, deleted_at: deletedAt }
          : m
      ));

      // Feedback para o usuário
      if (deleteForEveryone) {
        if (deletedOnWhatsApp) {
          toast.success('Mensagem apagada para todos');
        } else {
          toast.success('Mensagem apagada localmente', {
            description: 'Não foi possível apagar no WhatsApp'
          });
        }
      } else {
        toast.success('Mensagem apagada para mim');
      }

      chatLogger.debug('[handleDeleteMessage] Mensagem deletada. WhatsApp:', deletedOnWhatsApp);
    } catch (error) {
      console.error('[handleDeleteMessage] Error:', error);
      toast.error('Erro ao deletar mensagem');
    }
  };

  /**
   * Handler para reenviar mensagem que falhou ou travou
   * Verifica se já foi enviada antes de reenviar fisicamente para evitar duplicação
   */
  const handleResendMessage = async (failedMessage: Message) => {
    if (!selectedConversation || !user) return;

    try {
      chatLogger.debug('[handleResendMessage] Verificando mensagem antes de reenviar:', failedMessage.id);

      // ========== VERIFICAÇÃO PRÉ-REENVIO PARA EVITAR DUPLICAÇÃO ==========
      
      // Verifica se há erro assíncrono no metadata (ex: erro 131053 do Meta)
      // Mesmo tendo external_id, se tiver erro, DEVE reenviar fisicamente
      const hasAsyncError = !!(failedMessage.metadata as Record<string, unknown> | null)?.error;
      
      // 1. Verifica se mensagem já tem external_id (já foi enviada com sucesso)
      // IMPORTANTE: Se tiver erro assíncrono, ignora external_id e reenvia
      if (failedMessage.external_id && !hasAsyncError) {
        chatLogger.debug('[handleResendMessage] Mensagem já possui external_id sem erro, apenas atualizando status');
        
        await markSent({ messageId: failedMessage.id });
        
        setMessages(prev => prev.map(m => 
          m.id === failedMessage.id ? { ...m, status: 'sent' } : m
        ));
        
        showToast.success('Mensagem já havia sido enviada!', 'Status atualizado.');
        return; // NÃO reenvia fisicamente
      }

      // Se tem external_id MAS tem erro assíncrono, loga e continua para reenvio
      if (failedMessage.external_id && hasAsyncError) {
        chatLogger.debug('[handleResendMessage] Mensagem com external_id mas com erro assíncrono - reenviando fisicamente');
      }

      // 2. Verifica nos logs de observabilidade se já foi enviada com sucesso
      // Só faz essa verificação se NÃO tiver erro assíncrono
      if (!hasAsyncError) {
        const { data: successLog } = await supabase
          .from('api_request_logs')
          .select('external_id')
          .eq('message_id', failedMessage.id)
          .eq('status', 'success')
          .not('external_id', 'is', null)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        
        if (successLog?.external_id) {
          chatLogger.debug('[handleResendMessage] Log de sucesso encontrado, sincronizando status');
          
          // Atualiza mensagem com external_id encontrado nos logs (RPC Fase 4)
          await markSent({
            messageId: failedMessage.id,
            externalId: successLog.external_id,
          });
          
          setMessages(prev => prev.map(m => 
            m.id === failedMessage.id 
              ? { ...m, status: 'sent', external_id: successLog.external_id } 
              : m
          ));
          
          showToast.success('Mensagem já havia sido enviada!', 'Status sincronizado com sucesso.');
          return; // NÃO reenvia fisicamente
        }
      }

      // ========== CONTINUA COM REENVIO FÍSICO (mensagem realmente não foi enviada) ==========
      chatLogger.debug('[handleResendMessage] Nenhum envio anterior encontrado, reenviando fisicamente...');

      // 3. Atualiza status para 'pending' via RPC segura (feedback visual imediato)
      await resetForRetry(failedMessage.id);

      // Atualiza UI local
      setMessages(prev => prev.map(m => 
        m.id === failedMessage.id ? { ...m, status: 'pending' } : m
      ));

      // 2. Determina o destino (contato individual ou grupo)
      const phone = selectedConversation.contact?.phone 
        || selectedConversation.group?.external_id || '';

      // 3. Prepara payload baseado no tipo de mensagem
      const payload: Record<string, unknown> = {
        inbox_id: selectedConversation.inbox_id,
        phone,
        message_type: failedMessage.message_type,
      };

      // 4. Adiciona dados específicos por tipo de mensagem
      if (failedMessage.message_type === 'text') {
        payload.content = failedMessage.content;
      } else if (failedMessage.message_type === 'template') {
        // Para templates, extrai TODOS os dados do metadata (incluindo parâmetros de botão)
        const meta = failedMessage.metadata as { 
          template_name?: string; 
          template_language?: string; 
          template_params?: unknown[];
          body_params?: unknown[];
          header_params?: unknown[];
          button_params?: unknown[];
          order_details_params?: unknown;
          copy_code_params?: { code?: string };
        } | null;
        
        if (meta?.template_name) {
          payload.template_name = meta.template_name;
          payload.template_language = meta.template_language || 'pt_BR';
          // Compatibilidade: suporta template_params (legado) e body_params (atual)
          payload.template_params = meta.template_params || meta.body_params || [];
          payload.body_params = meta.body_params || meta.template_params || [];
          // CORREÇÃO: Preservar parâmetros de header, botão e PIX para reenvio correto
          if (meta.header_params) payload.header_params = meta.header_params;
          if (meta.button_params) payload.button_params = meta.button_params;
          if (meta.order_details_params) payload.order_details_params = meta.order_details_params;
          if (meta.copy_code_params) payload.copy_code_params = meta.copy_code_params;
        } else {
          chatLogger.error('[handleResendMessage] Template sem template_name no metadata');
          toast.error('Não foi possível reenviar: dados do template incompletos');
          return;
        }
      } else if (failedMessage.attachments?.length) {
        const attachment = failedMessage.attachments[0];
        // Converte path relativo para URL pública
        let mediaUrl = attachment.url.startsWith('http') 
          ? attachment.url 
          : supabase.storage.from('anexos').getPublicUrl(attachment.url).data.publicUrl;
        
        // ÁUDIO + META: reprepara o arquivo antes de reenviar.
        // Único formato aceito é a nota de voz OGG/Opus canônica; se a
        // canonicalização falhar, o reenvio é abortado (sem fallback MP3).
        if (
          failedMessage.message_type === 'audio' &&
          providerRequiresMp3(selectedConversation.inbox?.provider_slug)
        ) {
          try {
            const originalResponse = await fetch(mediaUrl);
            const originalBlob = await originalResponse.blob();
            const prepared = await prepareAudioForProvider(
              originalBlob,
              selectedConversation.inbox?.provider_slug,
            );

            const preparedPath = `Chats/${selectedConversation.id}/audio_${Date.now()}.${prepared.extension}`;
            const { error: preparedUploadError } = await supabase.storage
              .from('anexos')
              .upload(preparedPath, prepared.blob, {
                contentType: prepared.mimeType,
                upsert: false,
              });

            if (preparedUploadError) throw preparedUploadError;

            mediaUrl = supabase.storage.from('anexos').getPublicUrl(preparedPath).data.publicUrl;
            payload.media_mime_type = prepared.mode === 'basic_mp3' ? 'audio/mpeg' : 'audio/ogg; codecs=opus';
            payload.waveform = prepared.isVoiceNote;
            chatLogger.debug('[handleResendMessage] Áudio preparado para reenvio', { mode: prepared.mode });
          } catch (conversionError) {
            // Interrompe antes de chamar a Meta com um arquivo incompatível
            // (a API aceita o upload e devolve 131053 depois, de forma assíncrona).
            chatLogger.error('[handleResendMessage] Falha ao preparar áudio para reenvio', conversionError);
            throw conversionError;
          }
        }


        payload.media_url = mediaUrl;
        payload.media_caption = failedMessage.content || attachment.filename;

        
        // Para localização, extrai coordenadas do metadata
        if (failedMessage.message_type === 'location' && attachment.metadata) {
          const meta = attachment.metadata as { latitude?: number; longitude?: number; name?: string; address?: string };
          payload.latitude = meta.latitude;
          payload.longitude = meta.longitude;
          payload.location_name = meta.name;
          payload.location_address = meta.address;
        }
        
        // Para contato, extrai dados do metadata
        if (failedMessage.message_type === 'contact' && attachment.metadata) {
          const meta = attachment.metadata as { name?: string; phone?: string; businessDescription?: string };
          payload.contact_name = meta.name;
          payload.contact_phone = meta.phone;
          payload.business_description = meta.businessDescription;
        }
      }

      // 5. Reenvia via Edge Function (adiciona IDs de correlação para rastreabilidade)
      const { data: sendResult, error: sendError } = await supabase.functions.invoke('send-message', {
        body: {
          ...payload,
          // IDs de correlação para rastreabilidade na observabilidade
          source_type: 'chat',
          message_id: failedMessage.id,
          conversation_id: selectedConversation.id,
          contact_id: selectedConversation.contact?.id,
        },
      });

      // 6. Atualiza status baseado no resultado (RPCs Fase 4)
      if (sendError || !sendResult?.success) {
        chatLogger.error('[handleResendMessage] Falha no reenvio:', sendError || sendResult);
        await markFailed({
          messageId: failedMessage.id,
          errorPayload: { error: sendError ?? sendResult ?? null },
        });

        setMessages(prev => prev.map(m =>
          m.id === failedMessage.id ? { ...m, status: 'failed' } : m
        ));
        toast.error('Falha ao reenviar mensagem');
      } else {
        // Patch de metadata: limpa erro anterior, marca resent_at e guarda
        // referência ao external_id anterior. Mesclado no banco via RPC.
        const metadataPatch = {
          error: null,
          resent_at: new Date().toISOString(),
          original_external_id: failedMessage.external_id,
        };
        const currentMetadata = (failedMessage.metadata as Record<string, unknown> | null) || {};
        const newMetadata = { ...currentMetadata, ...metadataPatch };

        await markSent({
          messageId: failedMessage.id,
          externalId: sendResult.external_id,
          metadataPatch,
        });

        setMessages(prev => prev.map(m =>
          m.id === failedMessage.id
            ? { ...m, status: 'sent', external_id: sendResult.external_id, metadata: newMetadata }
            : m
        ));
        toast.success('Mensagem reenviada com sucesso!');
        chatLogger.debug('[handleResendMessage] Sucesso:', sendResult.external_id);
      }
    } catch (error) {
      chatLogger.error('[handleResendMessage] Erro:', error);
      // Best-effort: marca como failed via RPC; ignora erro secundário do RPC.
      try {
        await markFailed({
          messageId: failedMessage.id,
          errorPayload: { error: String((error as Error)?.message ?? error) },
        });
      } catch (e) {
        chatLogger.error('[handleResendMessage] markFailed também falhou:', e);
      }

      setMessages(prev => prev.map(m =>
        m.id === failedMessage.id ? { ...m, status: 'failed' } : m
      ));
      toast.error('Erro ao reenviar mensagem');
    }
  };

  // Hook para gerenciar blacklist LGPD (usado ao marcar como spam)
  const { addToBlacklist } = useBlacklist();

  /**
   * Atualiza o status de uma conversa (open, closed, spam, etc.)
   * Convertido para useCallback para evitar stale closure no handleCloseConversation
   */
  const handleUpdateConversationStatus = useCallback(async (status: ConversationStatus) => {
    if (!selectedConversation) return;

    try {
      // ========================================
      // NOVO: Mensagem de sistema ao encerrar conversa (webchat)
      // Informa o cliente no widget que a conversa foi encerrada
      // ========================================
      if (status === 'closed' && selectedConversation.inbox?.channel_type === 'webchat') {
        const agentName = profile?.full_name || user?.email?.split('@')[0] || 'Agente';
        
        // Insere mensagem de sistema via RPC segura (Fase 4)
        let sysMsg: { id: string } | null = null;
        let sysMsgError: unknown = null;
        try {
          sysMsg = await createSystem({
            conversationId: selectedConversation.id,
            content: `Conversa encerrada por ${agentName}`,
            senderId: user?.id || null,
          });
        } catch (err) {
          sysMsgError = err;
        }

        if (!sysMsgError && sysMsg) {
          chatLogger.info('[handleUpdateConversationStatus] Mensagem de sistema criada para encerramento', { messageId: sysMsg.id });
          
          // Dispara broadcast para o widget via send-message
          await supabase.functions.invoke('send-message', {
            body: {
              inbox_id: selectedConversation.inbox_id,
              phone: '',
              content: `Conversa encerrada por ${agentName}`,
              message_type: 'text',
              message_id: sysMsg.id,
              conversation_id: selectedConversation.id,
            },
          });
        } else if (sysMsgError) {
          chatLogger.warn('[handleUpdateConversationStatus] Erro ao criar mensagem de sistema:', sysMsgError);
        }
      }

      // Se marcando como SPAM, adicionar contato à blacklist LGPD automaticamente
      // Proteção LGPD: contato não receberá mais mensagens de campanhas
      if (status === 'spam' && selectedConversation.contact?.phone) {
        const blacklistSuccess = await addToBlacklist(
          selectedConversation.contact.phone,
          selectedConversation.contact.name || undefined,
          'spam' // Motivo: Marcado como spam
        );
        
        if (blacklistSuccess) {
          chatLogger.info('[handleUpdateConversationStatus] Contato adicionado à blacklist LGPD:', 
            selectedConversation.contact.phone
          );
        } else {
          // Contato pode já estar na blacklist - não é erro, apenas log
          chatLogger.warn('[handleUpdateConversationStatus] Contato não adicionado à blacklist (pode já existir):', 
            selectedConversation.contact.phone
          );
        }
      }

      const { error } = await supabase
        .from('conversations')
        .update({ 
          status,
          closed_at: status === 'closed' ? new Date().toISOString() : null
        })
        .eq('id', selectedConversation.id);

      if (error) throw error;

      // Se encerrando conversa, limpa a seleção para que a área do chat fique vazia
      // Isso reflete que a conversa saiu da lista lateral
      if (status === 'closed') {
        setSelectedConversation(null);
        // Limpa também as mensagens carregadas para liberar memória
        setMessages([]);
        toast.success('Conversa encerrada');
      } else {
        // Para outros status (spam, etc), apenas atualiza o estado
        setSelectedConversation(prev => prev ? { 
          ...prev, 
          status,
        } : null);
        toast.success(`Conversa marcada como ${status}`);
      }
    } catch (error) {
      console.error('Error updating conversation status:', error);
      toast.error('Erro ao atualizar status');
    }
  }, [selectedConversation, addToBlacklist, user, profile]);

  /**
   * Handler para encerrar conversa via botão no header
   * Usa handleUpdateConversationStatus como dependência para evitar stale closure
   */
  const handleCloseConversation = useCallback(() => {
    handleUpdateConversationStatus('closed');
  }, [handleUpdateConversationStatus]);

  /**
   * Inicia uma conversa com um contato a partir de mensagem compartilhada
   * Verifica se tem WhatsApp, cria contato/conversa se necessário
   */
  const handleStartConversationWithContact = async (phone: string, displayName: string) => {
    if (!selectedConversation || !currentTenant?.id) {
      toast.error('Selecione uma conversa primeiro');
      return;
    }
    
    try {
      // 1. Verificar se o número tem WhatsApp
      const { exists } = await checkPhoneExists(
        selectedConversation.inbox_id,
        phone
      );
      
      if (!exists) {
        toast.error('Este número não possui WhatsApp');
        return;
      }
      
      // 2. Criar ou obter o contato
      const { data: contactId, error: contactError } = await supabase.rpc(
        'get_or_create_contact',
        {
          _tenant_id: currentTenant.id,
          _phone: phone,
          _name: displayName,
        }
      );
      
      if (contactError) throw contactError;
      
      // 3. Criar ou obter a conversa
      const { data: conversationId, error: convError } = await supabase.rpc(
        'get_or_create_conversation',
        {
          _inbox_id: selectedConversation.inbox_id,
          _contact_id: contactId,
        }
      );
      
      if (convError) throw convError;
      
      // 4. Invalida cache para incluir nova conversa
      invalidateList();
      
      // 5. Buscar a conversa atualizada e selecionar
      const { data: newConvData } = await supabase
        .from('conversations')
        .select(`
          id,
          inbox_id,
          contact_id,
          assigned_to,
          department_id,
          status,
          last_message_at,
          last_message_preview,
          unread_count,
          created_at,
          contacts (
            id,
            name,
            phone,
            email,
            avatar_url,
            is_saved_to_agenda,
            metadata
          ),
          inboxes (
            id,
            name,
            channel_type,
            identifier,
            settings,
            api_providers:provider_id (
              slug
            )
          ),
          departments (
            id,
            name
          )
        `)
        .eq('id', conversationId)
        .single();
      
      if (newConvData) {
        const newConversation: Conversation = {
          id: newConvData.id,
          inbox_id: newConvData.inbox_id,
          contact_id: newConvData.contact_id,
          contact: {
            id: newConvData.contacts?.id || '',
            name: newConvData.contacts?.name || null,
            phone: newConvData.contacts?.phone || null,
            email: newConvData.contacts?.email || null,
            avatar_url: newConvData.contacts?.avatar_url || null,
            is_saved_to_agenda: newConvData.contacts?.is_saved_to_agenda || false,
            metadata: (newConvData.contacts?.metadata as Record<string, unknown>) || null,
            tags: [],
          },
          group_id: null, // Conversas iniciadas assim são sempre individuais
          group: null,
          inbox: {
            id: newConvData.inboxes?.id || '',
            name: newConvData.inboxes?.name || '',
            channel_type: newConvData.inboxes?.channel_type || '',
            identifier: newConvData.inboxes?.identifier || '',
            settings: newConvData.inboxes?.settings as Conversation['inbox']['settings'],
            provider_slug: (newConvData.inboxes as any)?.api_providers?.slug || null,
          },
          department_id: newConvData.department_id,
          department: newConvData.departments ? {
            id: newConvData.departments.id,
            name: newConvData.departments.name,
          } : null,
          assigned_to: newConvData.assigned_to,
          status: newConvData.status,
          last_message_at: newConvData.last_message_at,
          last_message_preview: newConvData.last_message_preview,
          unread_count: newConvData.unread_count,
          created_at: newConvData.created_at,
        };
        
        setSelectedConversation(newConversation);
        toast.success(`Conversa com ${displayName} aberta!`);
      }
      
    } catch (error) {
      console.error('Erro ao iniciar conversa:', error);
      toast.error('Erro ao iniciar conversa com o contato');
    }
  };

  /**
   * Handler para agente assumir conversa da fila
   * Atribui a conversa ao agente atual e muda status para 'open'
   */
  const handleClaimConversation = async () => {
    if (!selectedConversation || !user) return;

    try {
      chatLogger.info('Assumindo conversa', { 
        conversationId: selectedConversation.id, 
        userId: user.id 
      });

      const { error } = await supabase
        .from('conversations')
        .update({ 
          assigned_to: user.id,
          status: 'open' as ConversationStatus,
        })
        .eq('id', selectedConversation.id);

      if (error) throw error;

      // ========================================
      // NOVO: Mensagem de sistema ao assumir conversa (webchat)
      // Informa o cliente no widget que um agente iniciou o atendimento
      // ========================================
      if (selectedConversation.inbox?.channel_type === 'webchat') {
        const agentName = profile?.full_name || user?.email?.split('@')[0] || 'Agente';
        
        // Insere mensagem de sistema via RPC segura (Fase 4)
        let sysMsg: { id: string } | null = null;
        let sysMsgError: unknown = null;
        try {
          sysMsg = await createSystem({
            conversationId: selectedConversation.id,
            content: `${agentName} iniciou o atendimento`,
            senderId: user.id,
          });
        } catch (err) {
          sysMsgError = err;
        }

        if (!sysMsgError && sysMsg) {
          chatLogger.info('[handleClaimConversation] Mensagem de sistema criada para assumir conversa', { messageId: sysMsg.id });
          
          // Dispara broadcast para o widget via send-message
          await supabase.functions.invoke('send-message', {
            body: {
              inbox_id: selectedConversation.inbox_id,
              phone: '',
              content: `${agentName} iniciou o atendimento`,
              message_type: 'text',
              message_id: sysMsg.id,
              conversation_id: selectedConversation.id,
            },
          });
        } else if (sysMsgError) {
          chatLogger.warn('[handleClaimConversation] Erro ao criar mensagem de sistema:', sysMsgError);
        }
      }

      // Atualiza estado local da conversa selecionada
      setSelectedConversation(prev => prev ? { 
        ...prev, 
        assigned_to: user.id,
        status: 'open' as ConversationStatus,
        assigned_agent: {
          id: user.id,
          full_name: user.user_metadata?.full_name || user.email || null,
          avatar_url: null,
        }
      } : null);

      // Invalida cache para refletir mudança na lista
      invalidateList();

      toast.success('Conversa assumida com sucesso!');
    } catch (error) {
      chatLogger.error('Erro ao assumir conversa', error);
      toast.error('Erro ao assumir conversa');
    }
  };

  /**
   * Handler para enviar mensagem de template (WhatsApp Oficial - janela 24h expirada)
   * Insere mensagem com message_type='template' e envia via Edge Function
   */
  const handleSendTemplateMessage = async (params: {
    template_name: string;
    template_language: string;
    template_params: Array<{ type: string; text?: string }>;
    /** Parâmetros para botões URL dinâmicos */
    button_params?: Array<{
      type: 'button';
      sub_type: 'url';
      index: number;
      parameters: Array<{ type: 'text'; text: string }>;
    }>;
    /** Parâmetros PIX para botões ORDER_DETAILS */
    order_details_params?: {
      pix_code: string;
      merchant_name: string;
      pix_key: string;
      pix_key_type: 'CNPJ' | 'CPF' | 'EMAIL' | 'CELULAR' | 'ALEATORIA';
      amount_value: number;
      item_name?: string;
      reference_id?: string;
    };
    /** Botões do template para visualização no chat */
    template_buttons?: Array<{ type: string; text: string; url?: string; phone_number?: string }>;
    template_body: string;
    /** Formato do header de mídia (IMAGE, VIDEO, DOCUMENT) */
    header_format?: string;
    /** URL da mídia do header */
    header_media_url?: string;
  }) => {
    if (!selectedConversation || !user) return;

    try {
      // Monta o conteúdo de preview do template
      const previewContent = `📋 Template: ${params.template_name}`;

      // 1. Cria mensagem pendente via RPC segura (substitui INSERT direto em messages)
      const messageData = await createPending({
        conversationId: selectedConversation.id,
        messageType: 'template',
        content: previewContent,
        senderId: user.id,
        metadata: {
          template_name: params.template_name,
          template_language: params.template_language,
          template_params: params.template_params,
          template_body: params.template_body,
          // Dados de botões para visualização no chat
          template_buttons: params.template_buttons,
          button_params: params.button_params,
          order_details_params: params.order_details_params,
          // Header de mídia para renderização no chat
          header_format: params.header_format,
          header_media_url: params.header_media_url,
        },
      });

      // (validação de erro já é feita dentro da RPC create_outbound_message)

      // 2. ATUALIZAÇÃO OTIMISTA: Adiciona ao state local imediatamente como 'sent'
      const optimisticMessage: Message = {
        id: messageData.id,
        conversation_id: messageData.conversation_id,
        sender_type: messageData.sender_type as SenderType,
        sender_id: messageData.sender_id,
        content: messageData.content,
        message_type: messageData.message_type as MessageType,
        direction: messageData.direction as MessageDirection,
        status: 'sent', // ← OTIMISTA
        sent_at: messageData.sent_at,
        delivered_at: null,
        read_at: null,
        external_id: null,
        attachments: [],
        reply_to_id: null,
        reply_to: null,
        reactions: null,
        metadata: messageData.metadata as Record<string, unknown> | null,
        deleted_at: null,
        participant_phone: null,
      };
      setMessages(prev => [...prev, optimisticMessage]);

      // 3. Envia via Edge Function em background (inclui button_params e order_details_params)
      const { data: sendResult, error: sendError } = await supabase.functions.invoke('send-message', {
        body: {
          inbox_id: selectedConversation.inbox_id,
          phone: selectedConversation.contact?.phone || '',
          message_type: 'template',
          template_name: params.template_name,
          template_language: params.template_language,
          template_params: params.template_params,
          // NÃO enviar header_params — o send-message usa header_media_url do banco como fonte de verdade
          // Isso evita sobrescrever a URL permanente do Supabase com URLs temporárias expiradas da Meta
          button_params: params.button_params,
          order_details_params: params.order_details_params,
          source_type: 'chat',
          message_id: messageData.id,
          conversation_id: selectedConversation.id,
          contact_id: selectedConversation.contact?.id,
        },
      });

      // 4. Atualiza status baseado no resultado
      if (sendError || !sendResult?.success) {
        chatLogger.error('Erro ao enviar template:', sendError || sendResult);
        setMessages(prev => prev.map(m => m.id === messageData.id ? { ...m, status: 'failed' } : m));
        
        // Extrai corpo real da resposta quando FunctionsHttpError (HTTP 4xx/5xx)
        // Sem isso, sendResult fica null e detalhes reais ficam em sendError.context.json()
        let templateErrorDetails = sendResult;
        if (!templateErrorDetails && sendError?.context) {
          try {
            templateErrorDetails = await sendError.context.json();
          } catch {
            templateErrorDetails = { error: sendError?.message || 'Erro desconhecido' };
          }
        }
        
        // Marca falha via RPC segura (substitui UPDATE direto em messages)
        await markFailed({
          messageId: messageData.id,
          errorPayload: createErrorMetadata(sendError, templateErrorDetails),
        });
        
        // Registro assíncrono no catálogo de erros (fire-and-forget)
        registerSendError({
          errorCode: templateErrorDetails?.error_code || templateErrorDetails?.code || '500',
          provider: selectedConversation?.inbox?.provider_slug || 'unknown',
          inboxId: selectedConversation?.inbox_id,
          messageId: messageData.id,
          conversationId: selectedConversation?.id,
          messageType: 'template',
          rawError: templateErrorDetails,
          providerMessage: templateErrorDetails?.error || templateErrorDetails?.details,
        });
        
        toast.error('Erro ao enviar template');
      } else {
        setMessages(prev => prev.map(m => m.id === messageData.id ? { ...m, external_id: sendResult.external_id } : m));
        // Marca sucesso via RPC segura (substitui UPDATE direto em messages)
        await markSent({ messageId: messageData.id, externalId: sendResult.external_id });
        setShowTemplateDialog(false);
        toast.success('Template enviado com sucesso!');
      }
    } catch (error) {
      chatLogger.error('Erro ao enviar template:', error);
      toast.error('Erro ao enviar template');
    }
  };

  // filteredConversations REMOVIDO - filtros agora aplicados server-side via RPC get_conversations_list

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-7rem)] gap-0 md:gap-3 overflow-x-auto overflow-y-hidden">
      {/* Conversation List - Em mobile: visível apenas quando mobileView='list' */}
      <div className={cn(
        // Desktop: sempre visível com largura automática, flex-shrink-0 para manter tamanho no scroll
        "hidden md:block md:w-auto h-full flex-shrink-0",
        // Mobile: condicional baseado no mobileView
        mobileView === 'list' && "block w-full"
      )}>
        <ConversationList
          conversations={conversations}
          totalCount={totalCount}
          selectedConversation={selectedConversation}
          onSelectConversation={handleSelectConversation}
          statusFilters={statusFilters}
          onStatusFiltersChange={setStatusFilters}
          scopeFilter={scopeFilter}
          onScopeFilterChange={setScopeFilter}
          searchQuery={searchInput}
          onSearchChange={setSearchInput}
          departments={departments}
          departmentFilter={departmentFilter}
          onDepartmentFilterChange={setDepartmentFilter}
          conversationTypeFilter={conversationTypeFilter}
          onConversationTypeFilterChange={setConversationTypeFilter}
          readStatusFilter={readStatusFilter}
          onReadStatusFilterChange={setReadStatusFilter}
          inboxes={inboxes}
          inboxFilter={inboxFilter}
          onInboxFilterChange={setInboxFilter}
          tenantId={currentTenant?.id}
          // Infinite scroll
          onLoadMore={() => fetchNextPage()}
          hasMore={!!hasNextPage}
          isFetchingMore={isFetchingNextPage}
          // Filtros Avançados
          agents={agents}
          agentFilter={agentFilter}
          onAgentFilterChange={setAgentFilter}
          tags={tags}
          tagFilter={tagFilter}
          onTagFilterChange={setTagFilter}
          phases={phases}
          phaseFilter={phaseFilter}
          onPhaseFilterChange={setPhaseFilter}
          categories={categories}
          categoryFilter={categoryFilter}
          onCategoryFilterChange={(categoryId) => {
            setCategoryFilter(categoryId);
            // Reseta fase ao mudar categoria (fase pode não pertencer à nova categoria)
            setPhaseFilter('all');
          }}
        />
      </div>

      {/* Chat Area - Em mobile: visível apenas quando mobileView='chat' */}
      <div className={cn(
        // Desktop: sempre visível e flex-1
        "hidden md:flex md:flex-1 h-full",
        // Mobile: condicional baseado no mobileView
        mobileView === 'chat' && "flex w-full"
      )}>
        <ChatArea
          conversation={selectedConversation}
          messages={messages}
          loading={messagesLoading}
          onSendMessage={handleSendMessage}
          onSendInternalNote={handleSendInternalNote}
          currentUserName={profile?.full_name || user?.email?.split('@')[0] || 'Usuário'}

          onSendAudio={handleSendAudio}
          onSendImages={handleSendImages}
          onSendVideo={handleSendVideo}
          onSendDocument={handleSendDocument}
          onSendLocation={handleSendLocation}
          onSendContact={handleSendContact}
          onSendPix={handleSendPix}
          onSendReaction={handleSendReaction}
          onDeleteMessage={handleDeleteMessage}
          onResendMessage={handleResendMessage}
          onUpdateStatus={handleUpdateConversationStatus}
          onToggleSidebar={handleToggleContactSidebar}
          showSidebar={showContactSidebar}
          onTyping={handleTyping}
          contactPresence={contactPresence}
          tenantId={currentTenant?.id}
          onStartConversationWithContact={handleStartConversationWithContact}
          onClaimConversation={handleClaimConversation}
          currentUserId={user?.id}
          isAdminOrSupervisor={userRole !== 'agent'}
          isTenantAdmin={userRole === 'tenant_admin' || userRole === 'root_admin'}
          isSessionWindowExpired={sessionWindow.isExpired}
          onOpenTemplateDialog={() => setShowTemplateDialog(true)}
          hasMoreMessages={hasMoreMessages}
          loadingMoreMessages={loadingMoreMessages}
          onLoadMoreMessages={handleLoadMoreMessages}
          totalMessagesCount={totalMessagesCount}
          onRefreshConversation={() => invalidateList()}
          restrictedInboxIds={restrictedInboxIds}
          canUseObserverMode={canUseObserverMode}
          isObserverModeActive={isObserverModeActive}
          onToggleObserverMode={() => setIsObserverModeActive(!isObserverModeActive)}
          isTogglingBotPause={isTogglingBotPause}
          onToggleBotPause={handleToggleBotPause}
          hasPendingTransfer={hasPendingTransfer}
          onStartConversation={handleStartConversation}
          onCloseConversation={handleCloseConversation}
          isStartingConversation={isStartingConversation}
          onBackToList={handleBackToList}
          // ===== WEBRTC SCREEN SHARE =====
          currentTenantId={currentTenant?.id}
          currentAgentName={profile?.full_name || user?.email}
          currentAgentAvatar={getStorageUrl(profile?.avatar_url)}
          // v1.2.0: Fixar/desfixar a conversa atual no header do chat
          onPinConversation={pinConversation}
          onUnpinConversation={unpinConversation}
          isPinning={isPinning}
          // ===== MODO ANCORADO (Jump-to-message / Pesquisa contextual) =====
          isAnchoredMode={isAnchoredMode}
          hasMoreNewer={hasMoreNewer}
          loadingMoreNewer={loadingMoreNewer}
          newMessagesWhileAnchored={newMessagesWhileAnchored}
          jumpingToMessageId={jumpingToMessageId}
          onLoadMoreNewer={handleLoadMoreNewer}
          onExitAnchorMode={handleExitAnchorMode}
          onJumpToMessage={handleJumpToMessage}
        />
      </div>

      {/* Sidebar - Condicional para Grupo ou Contato */}
      {/* Em mobile: aparece como overlay em tela cheia quando showContactSidebar e mobileView='chat' */}
      {showContactSidebar && selectedConversation && (
        <div className={cn(
          // Desktop: sempre visível, posicionamento normal, flex-shrink-0 para manter tamanho no scroll
          "hidden md:block md:relative md:inset-auto md:z-auto flex-shrink-0",
          // Mobile: overlay de tela cheia APENAS quando mobileView='chat'
          mobileView === 'chat' && "fixed inset-0 z-50 bg-background block"
        )}>
          {selectedConversation.group_id && selectedConversation.group ? (
            <GroupSidebar
              group={selectedConversation.group}
              conversation={selectedConversation}
              onClose={handleCloseContactSidebar}
            />
          ) : selectedConversation.contact ? (
            <ContactSidebar
              contact={selectedConversation.contact}
              conversation={selectedConversation}
              onClose={handleCloseContactSidebar}
              lastInboundAt={selectedConversation.last_inbound_at}
              onRefreshConversation={refreshSelectedConversation}
              onToggleBotPause={handleToggleBotPause}
              isTogglingBotPause={isTogglingBotPause}
              onNavigateToConversation={async (conversationId, inboxId) => {
                // Troca inbox se necessário
                if (inboxId !== inboxFilter) {
                  setInboxFilter(inboxId);
                }
                // Busca conversa completa e seleciona
                const { data } = await supabase
                  .from('conversations')
                  .select(`
                    *,
                    contacts(*),
                    inboxes(id, name, channel_type, identifier, settings, api_providers:provider_id(slug)),
                    departments(id, name),
                    profiles:assigned_to(id, full_name, avatar_url)
                  `)
                  .eq('id', conversationId)
                  .single();
                
                if (data) {
                  const formattedConv: Conversation = {
                    id: data.id,
                    inbox_id: data.inbox_id,
                    contact_id: data.contact_id,
                    contact: data.contacts ? {
                      id: data.contacts.id,
                      name: data.contacts.name,
                      phone: data.contacts.phone,
                      email: data.contacts.email,
                      avatar_url: data.contacts.avatar_url,
                      is_saved_to_agenda: data.contacts.is_saved_to_agenda,
                      tags: [],
                    } : null,
                    group_id: data.group_id,
                    group: null,
                    inbox: {
                      id: data.inboxes?.id || '',
                      name: data.inboxes?.name || '',
                      channel_type: data.inboxes?.channel_type || '',
                      identifier: data.inboxes?.identifier || '',
                      settings: data.inboxes?.settings as Conversation['inbox']['settings'],
                      provider_slug: (data.inboxes as any)?.api_providers?.slug || null,
                    },
                    assigned_to: data.assigned_to,
                    // Acessa dados do agente via propriedade 'profiles' retornada pelo Supabase
                    assigned_agent: (data as any).profiles ? {
                      id: (data as any).profiles.id,
                      full_name: (data as any).profiles.full_name,
                      avatar_url: (data as any).profiles.avatar_url,
                    } : null,
                    department_id: data.department_id,
                    department: data.departments,
                    status: data.status,
                    last_message_at: data.last_message_at,
                    last_message_preview: data.last_message_preview,
                    unread_count: data.unread_count,
                    created_at: data.created_at,
                    has_non_campaign_messages: data.has_non_campaign_messages,
                    last_inbound_at: data.last_inbound_at,
                    // Human Takeover: estado de pausa do bot
                    bot_paused: data.bot_paused || false,
                    bot_paused_at: data.bot_paused_at || null,
                    bot_paused_until: data.bot_paused_until || null,
                  };
                  setSelectedConversation(formattedConv);
                }
              }}
            />
          ) : null}
        </div>
      )}

      {/* Modal de seleção de template (janela 24h expirada) */}
      <SendTemplateDialog
        open={showTemplateDialog}
        onOpenChange={setShowTemplateDialog}
        inboxId={selectedConversation?.inbox_id}
        onSendTemplate={handleSendTemplateMessage}
      />
    </div>
  );
};

export default TenantChat;
